// Core side-scrolling platformer engine for Nica Bros (photoreal assets + audio + boss).
import { GAME, buildLevel } from "./config";
import { drawBackground } from "./backgrounds";
import { ready } from "./assets";
import { GAME_STRINGS } from "./strings";
import {
  drawCharacter, drawDog, drawAbuela, drawChancleta, drawBeer, drawBottle, drawDanceSpot, drawGoal,
} from "./sprites";

const { WIDTH, HEIGHT, GRAVITY, MAX_FALL } = GAME;
const STEP_MS = 1000 / 60; // fixed simulation timestep (frame-rate independent movement)

export default class Game {
  constructor(canvas, { character, levelMeta, assets, audio, lang, diff, sponsorAds, bonusLives, callbacks }) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.ctx.imageSmoothingEnabled = true;
    this.ctx.imageSmoothingQuality = "high";
    this.scale = canvas.width / WIDTH;  // HiDPI backing scale for crisp rendering
    this.char = character;
    this.level = buildLevel(levelMeta, diff);
    this.assets = assets || {};
    this.audio = audio || null;
    this.S = GAME_STRINGS[lang] || GAME_STRINGS.es;
    this.lang = lang === "en" ? "en" : "es";
    this.sponsorAds = sponsorAds || {}; // { start: {img,video,link,...}|null, end: {...}|null }
    this.bonusLives = bonusLives || 0;
    this.cb = callbacks || {};
    this.chest = null; // daily treasure chest — only shown after enableChest()
    this.chestOpened = false;
    // Brand logo shown on a billboard while its sponsor video/image is still loading.
    this.brandLogo = new Image();
    this.brandLogo.src = `${process.env.PUBLIC_URL || ""}/game-assets/nicabros-logo.webp`;

    this.input = { left: false, right: false, jumpQueued: false, jumpHeld: false, action: false, actionQueued: false, danceQueued: false };
    this.running = false;
    this.ended = false;
    this.paused = false;
    this.frame = 0;
    this.shake = 0;
    this.lastHud = 0;
    this.elapsedMs = 0;
    this._lastTs = null;

    this.particles = [];
    this.texts = [];
    this.projectiles = [];
    this.bottles = [];

    this.resetState();
    this.bindKeys();
    this.bindPointer();
  }

  resetState() {
    const sp = this.level.spawn;
    this.player = { x: sp.x, y: sp.y, vx: 0, vy: 0, w: 40, h: 58, facing: 1, onGround: false, jumpsLeft: this.char.jumps, state: "idle", animFrame: 0, pounding: false };
    this.camera = { x: 0 };
    this.score = 0; this.lives = this.level.diff.lives + this.bonusLives; this.maxLives = this.lives; this.beers = 0; this.fiesta = 0;
    this.powered = false; this.powerTimer = 0; this.invincible = 0;
    this.danceTimer = 0; this.danceCooldown = 0; this.actionCooldown = 0;
    this.lastSafe = { x: sp.x, y: sp.y };
    this.boss = this.level.boss ? { ...this.level.boss } : null;
    this.police = null; this.hoofT = 0;
    this.policeTimer = Math.round((320 + Math.random() * 240) * this.level.diff.policeMul);
    // Ambient weather (drifting clouds + wind-blown leaves)
    this.clouds = [];
    for (let i = 0; i < 5; i++) this.clouds.push({ x: Math.random() * WIDTH, y: 16 + Math.random() * 130, s: 0.6 + Math.random() * 0.9, sp: 0.12 + Math.random() * 0.28, a: 0.3 + Math.random() * 0.3 });
    this.leaves = [];
    this.leafTimer = 30;
  }

  // ---------------- Input ----------------
  bindKeys() {
    this._onKey = (e, down) => {
      const c = e.code;
      if (c === "ArrowLeft" || c === "KeyA") this.input.left = down;
      else if (c === "ArrowRight" || c === "KeyD") this.input.right = down;
      else if (c === "Space" || c === "ArrowUp" || c === "KeyW") { if (down) this.input.jumpQueued = true; this.input.jumpHeld = down; }
      else if (c === "KeyK" || c === "KeyX" || c === "ShiftLeft" || c === "ArrowDown") { if (down && !this.input.action) this.input.actionQueued = true; this.input.action = down; }
      else if (c === "KeyJ" || c === "KeyF") { if (down) this.input.danceQueued = true; }
      else return;
      if (["Space", "ArrowUp", "ArrowDown"].includes(c)) e.preventDefault();
    };
    this._kd = (e) => this._onKey(e, true);
    this._ku = (e) => this._onKey(e, false);
    window.addEventListener("keydown", this._kd);
    window.addEventListener("keyup", this._ku);
  }

  // Tappable/clickable sponsor billboard hotspot.
  bindPointer() {
    this._adClick = (e) => {
      if (!this.adRects || !this.adRects.length || this.ended || this.paused) return;
      const pt = e.changedTouches ? e.changedTouches[0] : e;
      if (!pt) return;
      const rect = this.canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const bx = (pt.clientX - rect.left) * (this.canvas.width / rect.width);
      const by = (pt.clientY - rect.top) * (this.canvas.height / rect.height);
      for (const r of this.adRects) {
        if (bx >= r.x && bx <= r.x + r.w && by >= r.y && by <= r.y + r.h) {
          this.cb.onAdClick && this.cb.onAdClick(r.slot);
          break;
        }
      }
    };
    this._adMove = (e) => {
      if (this.ended || this.paused) { this.canvas.style.cursor = ""; return; }
      const rects = this.adRects;
      if (!rects || !rects.length) { if (this.canvas.style.cursor) this.canvas.style.cursor = ""; return; }
      const rect = this.canvas.getBoundingClientRect();
      const bx = (e.clientX - rect.left) * (this.canvas.width / rect.width);
      const by = (e.clientY - rect.top) * (this.canvas.height / rect.height);
      const over = rects.some((r) => bx >= r.x && bx <= r.x + r.w && by >= r.y && by <= r.y + r.h);
      this.canvas.style.cursor = over ? "pointer" : "";
    };
    this.canvas.addEventListener("click", this._adClick);
    this.canvas.addEventListener("mousemove", this._adMove);
  }

  setInput(name, val) {
    if (name === "jump") { if (val) this.input.jumpQueued = true; this.input.jumpHeld = val; }
    else if (name === "action") { if (val && !this.input.action) this.input.actionQueued = true; this.input.action = val; }
    else if (name === "dance") { if (val) this.input.danceQueued = true; }
    else this.input[name] = val;
  }

  // ---------------- Lifecycle ----------------
  start() {
    if (this.running) return;
    this.running = true; this.paused = false;
    this.audio?.resume();
    this.loop = this.loop.bind(this);
    this.raf = requestAnimationFrame(this.loop);
  }
  pause() { this.paused = true; this.audio?.stopMusic(); this._pauseVideos(); }
  resume() { if (this.ended) return; this.paused = false; }
  setSponsorAds(ads) { this.sponsorAds = ads || {}; }

  // Spawn the daily treasure chest for this level (called by React once it knows the chest is claimable).
  enableChest() { if (this.level.chest && !this.chestOpened) this.chest = { x: this.level.chest.x, y: this.level.chest.y, taken: false }; }

  drawChest(ctx, x, y) {
    const bob = Math.sin(this.frame * 0.09) * 3;
    ctx.save();
    ctx.translate(x, y + bob);
    // warm glow
    const glow = ctx.createRadialGradient(23, 24, 4, 23, 24, 50);
    glow.addColorStop(0, "rgba(255,220,90,0.55)");
    glow.addColorStop(1, "rgba(255,220,90,0)");
    ctx.fillStyle = glow; ctx.fillRect(-26, -26, 98, 96);
    // body
    ctx.fillStyle = "#7a4a1e"; ctx.fillRect(2, 18, 42, 26);
    ctx.fillStyle = "#5c3616"; ctx.fillRect(2, 40, 42, 4);
    // domed lid
    ctx.fillStyle = "#8a5622";
    ctx.beginPath(); ctx.moveTo(2, 18); ctx.lineTo(2, 9); ctx.quadraticCurveTo(23, -4, 44, 9); ctx.lineTo(44, 18); ctx.closePath(); ctx.fill();
    // gold bands
    ctx.fillStyle = "#f0c24a"; ctx.fillRect(0, 15, 46, 5); ctx.fillRect(20, 1, 6, 43);
    ctx.strokeStyle = "#c8961f"; ctx.lineWidth = 1.5; ctx.strokeRect(0, 15, 46, 5);
    // lock
    ctx.fillStyle = "#ffe07a"; ctx.fillRect(19, 21, 8, 8);
    ctx.fillStyle = "#3a2a12"; ctx.fillRect(22, 24, 2, 3);
    // sparkle
    const sp = (Math.sin(this.frame * 0.2) + 1) / 2;
    ctx.fillStyle = `rgba(255,255,255,${0.5 + sp * 0.5})`;
    ctx.beginPath(); ctx.arc(10, 9, 2 + sp, 0, Math.PI * 2); ctx.fill();
    // bouncing down-arrow indicator above the chest
    const ay = -16 - Math.sin(this.frame * 0.12) * 4;
    ctx.fillStyle = "#FFEB3B"; ctx.strokeStyle = "#7a5a00"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(23, ay + 12); ctx.lineTo(15, ay); ctx.lineTo(31, ay); ctx.closePath();
    ctx.fill(); ctx.stroke();
    ctx.restore();
  }
  _pauseVideos() {
    for (const s of ["start", "end"]) {
      const v = this.sponsorAds[s] && this.sponsorAds[s].video;
      if (v && !v.paused) { try { v.pause(); } catch (e) {} }
    }
  }
  destroy() {
    this.running = false;
    cancelAnimationFrame(this.raf);
    this.audio?.stopMusic();
    this._pauseVideos();
    window.removeEventListener("keydown", this._kd);
    window.removeEventListener("keyup", this._ku);
    if (this._adClick) this.canvas.removeEventListener("click", this._adClick);
    if (this._adMove) this.canvas.removeEventListener("mousemove", this._adMove);
  }
  loop() {
    if (!this.running) return;
    const now = performance.now();
    if (!this.paused && !this.ended) {
      // Fixed-timestep accumulator: movement stays at a consistent real-world
      // speed regardless of device frame rate. Slow phones (sub-60fps) no longer
      // crawl, and 120Hz phones no longer run double-speed. Physics stay fixed-step.
      let frame = this._lastTs != null ? now - this._lastTs : STEP_MS;
      if (frame > 250) frame = 250; // avoid spiral-of-death after tab sleep/switch
      this._acc = (this._acc || 0) + frame;
      let steps = 0;
      while (this._acc >= STEP_MS && steps < 5) {
        this.elapsedMs += STEP_MS;
        this.update();
        this._acc -= STEP_MS;
        steps++;
      }
    }
    this._alpha = this._acc / STEP_MS; // leftover fraction for render interpolation
    this._lastTs = now;
    this.render();
    this.raf = requestAnimationFrame(this.loop);
  }

  // ---------------- Helpers ----------------
  sfx(n, vol) { this.audio?.sfx(n, vol); }

  // Horse & carriage: clip-clop gallop that starts ~2.8s before the carriage
  // spawns (before the warning banner) and gets louder as it closes in.
  updateHoofbeats() {
    if (this.level.hazardSprite !== "carriage") return;
    const pol = this.police;
    let vol = 0;
    if (!pol && this.policeTimer > 0 && this.policeTimer <= 170) vol = 0.25 + 0.45 * (1 - this.policeTimer / 170);
    else if (pol && pol.x + pol.w > this.player.x - 120) {
      const dist = Math.max(0, pol.x - (this.camera.x + WIDTH));
      vol = Math.min(1, 0.7 + 0.3 * (1 - Math.min(1, dist / 300)));
    }
    if (vol <= 0) { this.hoofT = 0; return; }
    const beat = this.hoofT % 30; // 3-beat gallop: DA-da-da … per half second
    if (beat === 0) this.sfx("hoofhi", vol);
    else if (beat === 7 || beat === 13) this.sfx("hoof", vol);
    this.hoofT++;
  }
  addScore(n) { this.score += Math.round(n * (this.powered ? 2 : 1)); }
  burst(x, y, colors, count = 14, power = 5) {
    for (let i = 0; i < count; i++) {
      const a = Math.random() * Math.PI * 2, s = Math.random() * power + 1;
      this.particles.push({ x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s - 2, life: 28 + Math.random() * 16, color: colors[(Math.random() * colors.length) | 0], size: 3 + ((Math.random() * 4) | 0), grav: 0.25 });
    }
  }
  popText(x, y, text, color = "#fff") { this.texts.push({ x, y, text, color, life: 50, vy: -1.2 }); }
  throwImpact(bt) {
    const cx = bt.x + bt.w / 2, cy = bt.y + bt.h / 2;
    this.sfx("pop");
    this.burst(cx, cy, ["#fff3c4", "#ffd24a", "#ff8a3a", "#ffffff"], 14, 6);
    this.popText(cx - 12, cy - 8, this.S.pow || "POW!", "#ffd24a");
    this.shake = Math.max(this.shake, 5);
  }


  hurt() {
    if (this.invincible > 0 || this.ended) return;
    if (this.powered) { this.popText(this.player.x, this.player.y - 10, this.S.protegido, "#ffd400"); return; }
    this.lives -= 1; this.invincible = 110;
    this.player.vy = -8; this.player.vx = -this.player.facing * 6; this.shake = 12;
    this.sfx("hurt");
    this.burst(this.player.x + 16, this.player.y + 20, ["#ff5252", "#ffeb3b"], 16, 6);
    this.popText(this.player.x, this.player.y - 10, this.S.ay, "#ff5252");
    if (this.lives <= 0) this.end(false);
  }

  end(win) {
    if (this.ended) return;
    this.ended = true;
    this.audio?.stopMusic();
    if (win) {
      this.addScore(1500 + this.lives * 500);
      this.shake = 16; this.sfx("win");
      this.burst(this.player.x + 16, this.player.y, ["#E91E63", "#FFEB3B", "#0067C6", "#4CAF50"], 50, 9);
      this.popText(this.player.x, this.player.y - 30, this.S.meta, "#ffd400");
    } else {
      this.sfx("gameover");
    }
    this.pushHud(true);
    const timeMs = Math.round(this.elapsedMs);
    if (win && this.cb.onWin) this.cb.onWin({ score: this.score, lives: this.lives, maxLives: this.maxLives, beers: this.beers, timeMs });
    if (!win && this.cb.onGameOver) this.cb.onGameOver({ score: this.score, beers: this.beers, timeMs });
  }

  pushHud(force) {
    const now = performance.now();
    if (!force && now - this.lastHud < 90) return;
    this.lastHud = now;
    if (this.cb.onHud) {
      this.cb.onHud({
        score: this.score, lives: this.lives, maxLives: this.maxLives, beers: this.beers, fiesta: this.fiesta, timeMs: this.elapsedMs,
        powered: this.powered, levelName: this.level.name, short: this.level.short,
        playerX: this.player.x,
        boss: this.boss && this.boss.active && this.boss.alive ? { hp: this.boss.hp, max: this.boss.maxHp } : null,
      });
    }
  }
  overlap(a, b) { return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y; }

  // ---------------- Update ----------------
  update() {
    this.frame++;
    // Snapshot pre-integration positions for render interpolation (smooth motion)
    this._prevCamX = this.camera.x;
    this._prevPx = this.player.x;
    this._prevPy = this.player.y;
    const p = this.player, ch = this.char;
    if (this.invincible > 0) this.invincible--;
    if (this.danceCooldown > 0) this.danceCooldown--;
    if (this.actionCooldown > 0) this.actionCooldown--;
    if (this.shake > 0) this.shake--;

    if (this.powered) {
      this.powerTimer--;
      this.fiesta = Math.max(0, (this.powerTimer / (10 * 60)) * 100);
      if (this.powerTimer <= 0) { this.powered = false; this.fiesta = 0; this.popText(p.x, p.y - 20, this.S.finFiesta, "#fff"); }
    }
    const speed = ch.speed * (this.powered ? 1.18 : 1);

    if (this.danceTimer > 0) { this.danceTimer--; p.vx *= 0.6; }
    else {
      if (this.input.left) { p.vx = -speed; p.facing = -1; }
      else if (this.input.right) { p.vx = speed; p.facing = 1; }
      else { p.vx *= 0.6; if (Math.abs(p.vx) < 0.2) p.vx = 0; }
    }

    if (this.input.jumpQueued) {
      this.input.jumpQueued = false;
      if (p.onGround) { p.vy = ch.jump; p.onGround = false; p.jumpsLeft = ch.jumps - 1; this.sfx("jump"); this.burst(p.x + 16, p.y + 44, ["#fff", "#ddd"], 6, 3); }
      else if (p.jumpsLeft > 0) { p.vy = ch.jump * 0.92; p.jumpsLeft--; this.sfx("jump"); this.burst(p.x + 16, p.y + 40, ["#9fdcff", "#fff"], 8, 4); }
    }
    if (this.input.actionQueued) { this.input.actionQueued = false; this.doAction(); }
    if (this.input.danceQueued) { this.input.danceQueued = false; if (p.onGround && this.danceCooldown <= 0) this.startDance(); }

    p.vy += GRAVITY; if (p.vy > MAX_FALL) p.vy = MAX_FALL;
    p.x += p.vx; this.collideAxis("x");
    // Capture pre-move foot position + fall speed so stomps stay reliable even on
    // fast double-jump descents (prevents tunneling past an enemy's hit zone).
    p.prevBottom = p.y + p.h;
    p.wasFalling = p.vy > 0.5;
    p.onGround = false; p.y += p.vy; this.collideAxis("y");

    if (p.onGround) {
      p.jumpsLeft = ch.jumps;
      if (p.pounding) { this.groundPound(); p.pounding = false; }
      const overPit = this.level.pits.some((pt) => p.x + 16 > pt.x && p.x + 16 < pt.x + pt.w);
      if (!overPit) this.lastSafe = { x: p.x, y: p.y };
    }
    if (p.x < 0) p.x = 0;
    if (p.x + p.w > this.level.width) p.x = this.level.width - p.w;

    if (p.y > HEIGHT + 80) {
      this.lives -= 1; this.shake = 10; this.sfx("hurt");
      if (this.lives <= 0) this.end(false);
      else { p.x = this.lastSafe.x; p.y = this.lastSafe.y - 40; p.vx = 0; p.vy = 0; this.invincible = 80; this.popText(p.x, p.y - 20, this.S.plop, "#9fdcff"); }
    }

    if (!p.onGround) p.state = "jump";
    else if (this.danceTimer > 0) p.state = "dance";
    else if (Math.abs(p.vx) > 0.5) p.state = "walk";
    else p.state = "idle";
    p.animFrame++;

    // running step dust
    if (p.state === "walk" && p.onGround && this.frame % 9 === 0) {
      this.particles.push({ x: p.x + p.w / 2 - p.facing * 12, y: p.y + p.h - 2, vx: -p.facing * (1 + Math.random()), vy: -Math.random() * 1.4, life: 18, color: "#dccba6", size: 3 + ((Math.random() * 2) | 0), grav: 0.08 });
    }

    // Special Guest signature flair: a subtle ability-themed trail while on screen.
    if (this.char.guest && this.frame % 13 === 0) {
      const ab = this.char.ability;
      const front = p.x + (p.facing > 0 ? p.w - 2 : 2);
      let cfg = null;
      if (ab === "starnote") cfg = { colors: ["#ffd24a", "#fff3c4", "#7e57c2"], y: -22, note: true };       // gold stars/notes from mic
      else if (ab === "notes") cfg = { colors: ["#7e57c2", "#b39ddb", "#ffd24a"], y: -22, note: true };       // purple music notes
      else if (ab === "electric") cfg = { colors: ["#00d8ff", "#a8f0ff", "#ffffff"], y: 8 };                  // electric sparks
      else if (ab === "tornado") cfg = { colors: ["#dfefff", "#9fc4e8", "#ffffff"], y: 6 };                   // wind wisps
      else if (ab === "nacatamal") cfg = { colors: ["#7cb342", "#c9a227", "#fff3c4"], y: 10 };                // tamal/banana-leaf bits
      else if (ab === "coconut") cfg = { colors: ["#00f2ea", "#8a5a2b", "#fff3c4"], y: 8 };                   // tropical/coco sparkle
      else if (ab === "ball") cfg = { colors: ["#ffffff", "#222222", "#ffd400"], y: p.h - 12 };               // pitch sparkle at feet
      else if (ab === "fritanga") cfg = { colors: ["#ffae00", "#ff5722", "#fff3c4"], y: 8 };                  // sizzling fritanga sparks
      if (cfg) {
        const col = cfg.colors[(Math.random() * cfg.colors.length) | 0];
        const my = p.y + cfg.y - Math.random() * 4;
        if (cfg.note)
          this.particles.push({ x: front, y: my, vx: p.facing * (0.25 + Math.random() * 0.45), vy: -0.5 - Math.random() * 0.5, life: 33, color: col, size: 4, grav: -0.01, note: true });
        else
          this.particles.push({ x: front + (Math.random() - 0.5) * 4, y: my, vx: p.facing * (0.2 + Math.random() * 0.6), vy: -0.3 - Math.random() * 0.7, life: 22, color: col, size: 2 + ((Math.random() * 2) | 0), grav: -0.006 });
      }
    }

    this.updateWeather();
    this.updateEnemies();
    this.updateBoss();
    this.updateBossDefeat();
    this.updatePolice();
    this.updateHoofbeats();
    this.updateProjectiles();
    this.updateCollectibles();
    this.updateParticles();

    const target = p.x + p.w / 2 - WIDTH * 0.4;
    this.camera.x += (target - this.camera.x) * 0.1;
    if (this.camera.x < 0) this.camera.x = 0;
    if (this.camera.x > this.level.width - WIDTH) this.camera.x = this.level.width - WIDTH;

    if (!this.boss && p.x + p.w / 2 >= this.level.goalX) this.end(true);
    this.pushHud(false);
  }

  collideAxis(axis) {
    const p = this.player;
    for (const pl of this.level.platforms) {
      if (axis === "x") {
        const box = { x: p.x, y: p.y + 3, w: p.w, h: p.h - 6 };
        if (this.overlap(box, pl)) { if (p.vx > 0) p.x = pl.x - p.w; else if (p.vx < 0) p.x = pl.x + pl.w; p.vx = 0; }
      } else {
        if (this.overlap(p, pl)) {
          if (p.vy > 0) { p.y = pl.y - p.h; p.vy = 0; p.onGround = true; }
          else if (p.vy < 0) { p.y = pl.y + pl.h; p.vy = 0; }
        }
      }
    }
  }

  doAction() {
    const p = this.player;
    if (this.char.ability === "throw") {
      if (this.actionCooldown > 0) return;
      this.actionCooldown = 24;
      this.bottles.push({ x: p.x + (p.facing > 0 ? p.w : -10), y: p.y + 14, vx: p.facing * 8, vy: -2, w: 14, h: 18 });
      this.sfx("beer");
      this.burst(p.x + p.w / 2, p.y + 16, ["#3a7d2c", "#ffd24a"], 5, 3);
    } else if (this.char.ability === "strength") {
      if (!p.onGround) { p.pounding = true; p.vy = 17; this.popText(p.x, p.y - 10, this.S.fuerza, "#ff8a3a"); }
      else if (this.actionCooldown <= 0) { this.actionCooldown = 30; this.groundPound(); }
    } else if (this.char.ability === "rock" || this.char.ability === "ball" || this.char.ability === "homerun" || this.char.ability === "notes" || this.char.ability === "fritanga" || this.char.ability === "nacatamal" || this.char.ability === "coconut" || this.char.ability === "electric" || this.char.ability === "bread" || this.char.ability === "cream" || this.char.ability === "tornado" || this.char.ability === "starnote" || this.char.ability === "hypno" || this.char.ability === "maraca") {
      if (this.actionCooldown > 0) return;
      this.actionCooldown = 20;
      const isBall = this.char.ability === "ball";
      const isHomerun = this.char.ability === "homerun";
      const isNote = this.char.ability === "notes";
      const isFritanga = this.char.ability === "fritanga";
      const isNaca = this.char.ability === "nacatamal";
      const isCoco = this.char.ability === "coconut";
      const isBolt = this.char.ability === "electric";
      const isBread = this.char.ability === "bread";
      const isCream = this.char.ability === "cream";
      const isTornado = this.char.ability === "tornado";
      const isStarNote = this.char.ability === "starnote";
      const isHypno = this.char.ability === "hypno";
      const isMaraca = this.char.ability === "maraca";
      const kind = isMaraca ? "maraca" : isHomerun ? "baseball" : isHypno ? "hypno" : isStarNote ? "starnote" : isTornado ? "tornado" : isCream ? "cream" : isBread ? "bread" : isBolt ? "bolt" : isCoco ? "coconut" : isNaca ? "nacatamal" : isFritanga ? "fritanga" : isNote ? "note" : isBall ? (this.char.ballKind || "ball") : "rock";
      const yOff = isStarNote ? -24 : isHypno ? 6 : isNote ? 2 : 10;
      const shots = (isBall && this.char.multiShot) ? this.char.multiShot : 1;
      if (shots > 1) {
        // Fan out several balls at different arcs (e.g. Jorge kicks 3 soccer balls).
        for (let s = 0; s < shots; s++) {
          const f = shots > 1 ? s / (shots - 1) : 0.5; // 0..1
          this.bottles.push({ x: p.x + (p.facing > 0 ? p.w - 2 : -8), y: p.y + yOff, vx: p.facing * (7.4 + f * 2.2), vy: -3.4 + f * 3.0, w: 18, h: 16, kind, rot: 0 });
        }
      } else {
        this.bottles.push({ x: p.x + (p.facing > 0 ? p.w - 2 : -8), y: p.y + yOff, vx: p.facing * (isHypno ? 9 : 8), vy: isStarNote ? -2.4 : isHypno ? 0 : -2, w: 18, h: 16, kind, rot: 0 });
      }
      this.sfx(isBolt || isTornado || isHypno || isHomerun ? "bosshit" : isCoco || isNaca || isFritanga || isNote || isBread || isCream || isStarNote || isMaraca ? "coin" : isBall ? "jump" : "bosshit");
      this.popText(p.x, p.y - 10, isMaraca ? (this.S.maraca || "¡MARACAZO!") : isHomerun ? (this.char.powText || "¡JONRÓN!") : isHypno ? (this.S.hypno || "¡HYPNO PUNCH!") : isStarNote ? (this.S.starnote || "¡STAR!") : isTornado ? (this.S.tornado || "RIGHT HAND KO!") : isCream ? (this.S.cream || "¡CREMAZO!") : isBread ? (this.S.bread || "¡PANAZO!") : isBolt ? (this.S.electric || "RIGHT HOOK!") : isCoco ? (this.S.coconut || "COCO") : isNaca ? this.S.nacatamal : isFritanga ? (this.char.powText || this.S.fritanga) : isNote ? this.S.notes : isBall ? this.S.ball : this.S.rock, isMaraca ? "#e0a82e" : isHomerun ? "#ffffff" : isHypno ? "#c44dff" : isStarNote ? "#ffd24a" : isTornado ? "#cfe8ff" : isCream ? "#fff3c4" : isBread ? "#d9a441" : isBolt ? "#00d8ff" : isCoco ? "#8a5a2b" : isNaca ? "#7cb342" : isFritanga ? "#ffae00" : isNote ? "#7e57c2" : isBall ? "#ffffff" : "#bdbdbd");
      this.burst(p.x + (p.facing > 0 ? p.w + 4 : -6), p.y + (isStarNote ? -20 : 14), isMaraca ? ["#e0a82e", "#8d6e63", "#fff3c4"] : isHomerun ? ["#ffffff", "#e53935", "#ffd400"] : isHypno ? ["#c44dff", "#ff77e0", "#ffffff"] : isStarNote ? ["#ffd24a", "#fff3c4", "#7e57c2"] : isTornado ? ["#dfefff", "#9fc4e8", "#ffffff"] : isCream ? ["#fffef5", "#fff3c4", "#ffffff"] : isBread ? ["#d9a441", "#b9772e", "#fff3c4"] : isBolt ? ["#00d8ff", "#a8f0ff", "#ffffff"] : isCoco ? ["#8a5a2b", "#c9a227", "#fff3c4"] : isNaca ? ["#7cb342", "#c9a227", "#fff3c4"] : isFritanga ? ["#ffae00", "#ff5722", "#fff3c4"] : isNote ? ["#7e57c2", "#ffd24a", "#ffffff"] : isBall ? ["#ffffff", "#222"] : ["#9e9e9e", "#cfcfcf"], isBolt || isTornado || isHypno || isHomerun ? 8 : 5, isBolt || isTornado || isHypno || isHomerun ? 4 : 3);
      if (isHomerun) { this.burst(p.x + (p.facing > 0 ? p.w + 10 : -12), p.y + 6, ["#ffffff", "#e53935", "#ffd400", "#ff9800"], 16, 6); }
    } else if (this.char.ability === "machete") {
      if (this.actionCooldown > 0) return;
      this.actionCooldown = 26;
      this.sfx("bosshit");
      this.popText(p.x, p.y - 10, this.S.machete, "#ffd24a");
      this.shake = 6;
      const reach = 110;
      this.burst(p.x + p.w / 2 + p.facing * 44, p.y + 22, ["#ffffff", "#ffd24a", "#c0c0c0"], 14, 7);
      for (const e of this.level.enemies) {
        if (!e.alive) continue;
        const d = e.x - p.x;
        const inFront = p.facing > 0 ? (d > -10 && d < reach + p.w) : (d < 10 && d > -(reach + p.w));
        if (inFront && Math.abs(e.y - p.y) < 70) this.defeatEnemy(e, true);
      }
      if (this.boss && this.boss.active && this.boss.alive) {
        const d = this.boss.x - p.x;
        const inFront = p.facing > 0 ? (d > -20 && d < reach + 80) : (d < 20 && d > -(reach + 80));
        if (inFront && Math.abs(this.boss.y - p.y) < 120) this.damageBoss(2);
      }
    } else if (this.char.ability === "chop") {
      if (this.actionCooldown > 0) return;
      this.actionCooldown = 22;
      this.sfx("bosshit");
      this.popText(p.x, p.y - 10, this.S.karate, "#00e5ff");
      this.burst(p.x + p.w / 2 + p.facing * 32, p.y + 24, ["#ffffff", "#00e5ff"], 12, 6);
      const reach = 80;
      for (const e of this.level.enemies) {
        if (!e.alive) continue;
        const d = e.x - p.x;
        const inFront = p.facing > 0 ? (d > -10 && d < reach + p.w) : (d < 10 && d > -(reach + p.w));
        if (inFront && Math.abs(e.y - p.y) < 64) this.defeatEnemy(e, true);
      }
      if (this.boss && this.boss.active && this.boss.alive) {
        const d = this.boss.x - p.x;
        const inFront = p.facing > 0 ? (d > -20 && d < reach + 70) : (d < 20 && d > -(reach + 70));
        if (inFront && Math.abs(this.boss.y - p.y) < 110) this.damageBoss(1);
      }
    } else if (this.char.ability === "headbutt") {
      if (this.actionCooldown > 0) return;
      this.actionCooldown = 24;
      this.sfx("bosshit");
      p.vx = p.facing * 9;
      this.popText(p.x, p.y - 10, this.S.headbutt || "¡CABEZAZO!", "#ffca28");
      this.burst(p.x + p.w / 2 + p.facing * 30, p.y + 8, ["#ffca28", "#ffffff"], 12, 6);
      const reach = 74;
      for (const e of this.level.enemies) {
        if (!e.alive) continue;
        const d = e.x - p.x;
        const inFront = p.facing > 0 ? (d > -10 && d < reach + p.w) : (d < 10 && d > -(reach + p.w));
        if (inFront && Math.abs(e.y - p.y) < 60) this.defeatEnemy(e, true);
      }
      if (this.boss && this.boss.active && this.boss.alive) {
        const d = this.boss.x - p.x;
        const inFront = p.facing > 0 ? (d > -20 && d < reach + 70) : (d < 20 && d > -(reach + 70));
        if (inFront && Math.abs(this.boss.y - p.y) < 110) this.damageBoss(1);
      }
    } else if (this.char.ability === "spin") {
      if (this.actionCooldown > 0) return;
      this.actionCooldown = 26;
      this.sfx("bosshit");
      this.popText(p.x, p.y - 10, this.S.spin || "¡GIRO!", "#ff3da6");
      this.shake = 8;
      p.spinTimer = 20;
      const reach = 100;
      this.burst(p.x + p.w / 2, p.y + 24, ["#ff3da6", "#FFEB3B", "#ffffff"], 18, 6);
      for (const e of this.level.enemies) {
        if (!e.alive) continue;
        if (Math.abs(e.x - p.x) < reach && Math.abs(e.y - p.y) < 84) this.defeatEnemy(e, true);
      }
      if (this.boss && this.boss.active && this.boss.alive && Math.abs(this.boss.x - p.x) < reach + 40 && Math.abs(this.boss.y - p.y) < 120) this.damageBoss(1);
    } else {
      if (this.actionCooldown > 0) return;
      this.actionCooldown = 36;
      p.vx = p.facing * 13; this.invincible = Math.max(this.invincible, 14);
      this.sfx("jump");
      this.burst(p.x + 16, p.y + 24, ["#9fdcff", "#fff"], 10, 5);
      this.popText(p.x, p.y - 10, this.S.zoom, "#3aa657");
    }
  }

  groundPound() {
    const p = this.player;
    this.shake = 12; this.sfx("bosshit");
    this.burst(p.x + 16, p.y + 44, ["#ff8a3a", "#ffeb3b", "#fff"], 22, 7);
    for (const e of this.level.enemies) {
      if (!e.alive) continue;
      if (Math.abs(e.x - p.x) < 140 && Math.abs(e.y - p.y) < 90) this.defeatEnemy(e, true);
    }
    if (this.boss && this.boss.active && this.boss.alive && Math.abs(this.boss.x - p.x) < 170 && Math.abs(this.boss.y - p.y) < 120) this.damageBoss(1);
  }

  startDance() {
    this.danceTimer = 28; this.danceCooldown = 40;
    const p = this.player;
    this.addScore(60); this.sfx("dance");
    for (let i = 0; i < 8; i++) {
      this.particles.push({ x: p.x + 16, y: p.y + 8, vx: (Math.random() - 0.5) * 4, vy: -2 - Math.random() * 3, life: 40, color: ["#E91E63", "#FFEB3B", "#0067C6", "#4CAF50"][(Math.random() * 4) | 0], size: 5, grav: 0.02, note: true });
    }
    for (const d of this.level.danceSpots) {
      if (!d.used && Math.abs(d.x - (p.x + 16)) < 40) { d.used = true; this.fiesta = Math.min(100, this.fiesta + 20); this.addScore(150); this.popText(d.x, d.y - 60, this.S.plusFiesta, "#E91E63"); }
    }
    this.popText(p.x, p.y - 20, this.S.baila, "#FFEB3B");
    if (this.fiesta >= 100 && !this.powered) this.activateFiesta();
  }

  activateFiesta() {
    this.powered = true; this.powerTimer = 10 * 60; this.shake = 14; this.sfx("fiesta");
    const p = this.player;
    this.burst(p.x + 16, p.y + 20, ["#ffd400", "#E91E63", "#fff"], 40, 8);
    this.popText(p.x, p.y - 30, this.char.powText || this.S.fiestaMode, "#ffd400");
  }

  // ---------------- Enemies ----------------
  updateWeather() {
    // drifting clouds wrap across the sky
    for (const c of this.clouds) {
      c.x += c.sp;
      if (c.x - 80 * c.s > WIDTH) { c.x = -80 * c.s; c.y = 16 + Math.random() * 130; }
    }
    // wind-blown leaves stream across the screen
    this.leafTimer--;
    if (this.leafTimer <= 0) {
      this.leafTimer = 8 + Math.random() * 22;
      this.leaves.push({
        x: -20, y: 50 + Math.random() * (HEIGHT - 220),
        vx: 2.4 + Math.random() * 2.8, sway: Math.random() * Math.PI * 2,
        swaySpd: 0.05 + Math.random() * 0.06, amp: 0.5 + Math.random() * 1.5,
        size: 4 + Math.random() * 5, rot: Math.random() * Math.PI,
        color: ["#5a8f3a", "#7fae4a", "#c9a23a", "#b5651d"][(Math.random() * 4) | 0],
        life: 220 + Math.random() * 140,
      });
    }
    for (const lf of this.leaves) {
      lf.sway += lf.swaySpd;
      lf.x += lf.vx;
      lf.y += Math.sin(lf.sway) * lf.amp + 0.2;
      lf.rot += 0.14;
      lf.life--;
    }
    this.leaves = this.leaves.filter((lf) => lf.life > 0 && lf.x < WIDTH + 40);
  }

  updateEnemies() {
    const p = this.player;
    for (const e of this.level.enemies) {
      if (!e.alive) continue;
      if (e.type === "dog") {
        if (e.charger) {
          const dx = p.x - e.x;
          if (Math.abs(dx) < 230 && Math.abs(p.y - e.y) < 80) {
            e.dir = dx > 0 ? 1 : -1;
            e.x += e.dir * (e.speed + 2.6);
            if (!e.charging) { e.charging = true; this.popText(e.x + 10, e.y - 14, this.S.embiste, "#ff5252"); }
          } else {
            e.charging = false;
            e.x += e.dir * e.speed;
            if (e.x > e.baseX + e.range + 80) e.dir = -1;
            if (e.x < e.baseX - e.range - 80) e.dir = 1;
          }
        } else {
          e.x += e.dir * e.speed;
          if (e.x > e.baseX + e.range) e.dir = -1;
          if (e.x < e.baseX - e.range) e.dir = 1;
        }
      } else if (e.type === "abuela") {
        e.throwT--;
        const onScreen = e.x > this.camera.x - 40 && e.x < this.camera.x + WIDTH + 40;
        if (e.throwT <= 0 && onScreen) {
          e.throwT = Math.round((e.rapid ? 80 : 120) * this.level.diff.throwMul);
          const dir = p.x < e.x ? -1 : 1;
          const shots = e.rapid ? [-1, 1] : [0];
          for (const k of shots) {
            this.projectiles.push({ x: e.x + (dir > 0 ? 26 : -10), y: e.y + 8, vx: dir * (5.2 + k * 0.6), vy: -4 - Math.abs(k), w: 14, h: 6, rot: 0 });
          }
        }
      }
      const eb = this.enemyBox(e);
      if (this.overlap(p, eb)) {
        // Forgiving, tunnel-proof stomp: player was descending and their feet
        // were above the enemy's upper body before this frame's movement.
        const stomp = (p.wasFalling || p.vy > 0.5) && p.prevBottom <= eb.y + eb.h * 0.6;
        if (this.powered) this.defeatEnemy(e);
        else if (stomp) this.stompEnemy(e, eb);
        else this.hurt();
      }
    }
  }
  // Squash an enemy with a smooth springy bounce; refresh air-jumps so players
  // can chain stomps across double jumps.
  stompEnemy(e, eb) {
    const p = this.player;
    this.defeatEnemy(e);
    p.vy = this.input.jumpHeld ? -13 : -10.5;
    p.onGround = false;
    p.jumpsLeft = this.char.jumps;
    this.shake = Math.max(this.shake, 5);
    this.burst(eb.x + eb.w / 2, eb.y + eb.h, ["#fff", "#ffd24a"], 8, 4);
  }
  enemyBox(e) { return e.type === "dog" ? { x: e.x, y: e.y - (e.charger ? 8 : 4), w: e.charger ? 54 : 48, h: e.charger ? 42 : 38 } : { x: e.x - 2, y: e.y - 6, w: 42, h: 66 }; }
  defeatEnemy(e, silent) {
    e.alive = false; this.addScore(200); this.sfx("defeat");
    const b = this.enemyBox(e);
    this.burst(b.x + b.w / 2, b.y + b.h / 2, ["#fff", "#ffeb3b", "#ff5722"], 18, 6);
    if (!silent) this.popText(b.x, b.y - 10, "+200", "#ffeb3b");
  }

  // ---------------- Boss ----------------
  updateBoss() {
    const b = this.boss, p = this.player;
    if (!b || !b.alive) return;
    if (!b.active) {
      if (p.x > b.triggerX) { b.active = true; this.shake = 16; this.sfx("bosshit"); this.popText(b.x + 20, b.y - 20, this.S.jefa, "#ff5252"); }
      else return;
    }
    b.vy += GRAVITY; b.y += b.vy;
    const groundTop = GAME.GROUND_Y - b.h;
    if (b.y > groundTop) { b.y = groundTop; b.vy = 0; }
    // Phase 2: enrage at half HP — faster, jumps more, wider/quicker throws
    if (!b.enraged && b.hp <= b.maxHp / 2) {
      b.enraged = true; this.shake = 18; this.sfx("bosshit");
      this.popText(b.x + 20, b.y - 24, this.S.enrage, "#ff3b3b");
      this.burst(b.x + b.w / 2, b.y + b.h / 2, ["#ff3b3b", "#ffeb3b", "#fff"], 28, 8);
    }
    const dir = p.x < b.x ? -1 : 1; b.dir = dir;
    const bossSpeedF = 1 + (this.level.diff.speedMul - 1) * 0.5;
    b.x += dir * (b.enraged ? 2.0 : 1.15) * bossSpeedF;
    b.jumpT--;
    if (b.jumpT <= 0 && b.y >= groundTop - 1) { b.vy = b.enraged ? -14 : -12.5; b.jumpT = (b.enraged ? 95 : 150) + Math.random() * 60; }
    b.throwT--; if (b.throwT <= 0) {
      b.throwT = Math.round((b.enraged ? 42 : 60) * this.level.diff.throwMul); const d = p.x < b.x ? -1 : 1;
      const spread = b.enraged ? 2 : 1;
      for (let k = -spread; k <= spread; k++) this.projectiles.push({ x: b.x + b.w / 2, y: b.y + 24, vx: d * (4 + k * 0.9), vy: -5 - Math.abs(k), w: 14, h: 6, rot: 0 });
    }
    if (b.hurtT > 0) b.hurtT--;
    const bb = { x: b.x + 14, y: b.y + 10, w: b.w - 28, h: b.h - 12 };
    if (this.overlap(p, bb)) {
      const stomp = (p.wasFalling || p.vy > 1) && p.prevBottom <= bb.y + 30;
      if (stomp) { this.damageBoss(2); p.vy = this.input.jumpHeld ? -14 : -12.5; p.onGround = false; p.jumpsLeft = this.char.jumps; }
      else if (this.powered) this.damageBoss(1);
      else this.hurt();
    }
  }
  damageBoss(n) {
    const b = this.boss;
    if (!b || b.hurtT > 0 || !b.alive) return;
    b.hp -= n; b.hurtT = 35; this.shake = 12; this.sfx("bosshit");
    this.burst(b.x + b.w / 2, b.y + b.h / 2, ["#fff", "#ff5252", "#ffeb3b"], 22, 7);
    this.popText(b.x + 30, b.y - 10, this.S.golpe, "#ff5252");
    if (b.hp <= 0) {
      b.alive = false; b.defeated = true; b.defeatT = 0;
      this.shake = 16; this.sfx("win"); this.invincible = 999;
      this.burst(b.x + b.w / 2, b.y + b.h / 2, ["#E91E63", "#FFEB3B", "#0067C6", "#4CAF50"], 70, 11);
    }
  }

  // ~3s funny defeat gag for La Jefa: she wobbles dizzy, then topples on her
  // back with stars spinning + a sandal flying off, before the level ends.
  updateBossDefeat() {
    const b = this.boss;
    if (!b || !b.defeated || this.ended) return;
    b.defeatT++;
    if (b.defeatT % 6 === 0) this.burst(b.x + b.w / 2 + (Math.random() - 0.5) * b.w, b.y + b.h * 0.4, ["#FFEB3B", "#fff"], 4, 3);
    if (b.defeatT > 180) this.end(true);
  }

  drawBossDefeatMain(b) {
    const ctx = this.ctx;
    const img = this.assets.boss;
    const t = b.defeatT;
    const targetH = b.h + 50;
    let rot;
    if (t < 90) rot = Math.sin(t * 0.35) * 0.32;                          // dizzy wobble
    else rot = Math.min(1.4, (t - 90) * 0.03) + Math.sin(t * 0.3) * 0.03;  // topple onto her back
    const cx = b.x + b.w / 2;
    const cy = b.y + b.h * 0.5;
    if (ready(img)) {
      const scale = targetH / img.naturalHeight;
      const dw = img.naturalWidth * scale, dh = img.naturalHeight * scale;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(rot);
      ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
      ctx.restore();
    } else { ctx.fillStyle = "#7a3d8a"; ctx.fillRect(b.x, b.y, b.w, b.h); }
    // dizzy stars spinning over her head
    for (let i = 0; i < 3; i++) {
      const a = this.frame * 0.13 + i * (Math.PI * 2 / 3);
      this._drawStar(cx + Math.cos(a) * 34, b.y - 4 + Math.sin(a) * 12, 9, "#FFEB3B");
    }
    // a sandal flies off in an arc
    const ch = this.assets.chancleta;
    const ft = Math.min(120, t);
    const fx = cx + 30 + ft * 1.4;
    const fy = b.y - Math.sin(Math.min(Math.PI, ft * 0.035)) * 90 + ft * ft * 0.008;
    if (ready(ch)) { ctx.save(); ctx.translate(fx, fy); ctx.rotate(t * 0.25); ctx.drawImage(ch, -18, -12, 36, 24); ctx.restore(); }
  }

  _drawStar(x, y, r, color) {
    const ctx = this.ctx;
    ctx.save(); ctx.translate(x, y); ctx.fillStyle = color;
    ctx.beginPath();
    for (let i = 0; i < 5; i++) {
      const a = -Math.PI / 2 + i * (Math.PI * 2 / 5);
      ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
      const a2 = a + Math.PI / 5;
      ctx.lineTo(Math.cos(a2) * r * 0.45, Math.sin(a2) * r * 0.45);
    }
    ctx.closePath(); ctx.fill(); ctx.restore();
  }

  hazardWarnText() {
    const h = this.level.hazardSprite;
    return h === "moto" ? this.S.moto : h === "maypole" ? this.S.maypole : h === "tuktuk" ? this.S.tuktuk : h === "carriage" ? this.S.carriage : this.S.police;
  }

  // Flashing incoming-hazard banner (screen space) at the right edge: shows
  // ~1.7s before the hazard spawns and stays until it has entered the screen.
  drawHazardWarning() {
    const pol = this.police;
    const incoming = (!pol && this.policeTimer > 0 && this.policeTimer <= 100) || (pol && pol.x > this.camera.x + WIDTH - 60);
    if (!incoming) return;
    const ctx = this.ctx;
    const blink = Math.floor(this.frame / 8) % 2 === 0;
    const text = this.hazardWarnText();
    ctx.save();
    ctx.font = "800 22px 'Baloo 2', system-ui, sans-serif";
    const tw = ctx.measureText(text).width;
    const pw = tw + 74, ph = 40, px = WIDTH - pw - 14, py = GAME.GROUND_Y - 150;
    ctx.globalAlpha = 0.92;
    ctx.fillStyle = blink ? "#ffd400" : "#ff5a1f";
    ctx.beginPath(); ctx.roundRect(px, py, pw, ph, 20); ctx.fill();
    ctx.lineWidth = 3; ctx.strokeStyle = "#1a1030"; ctx.stroke();
    ctx.fillStyle = "#1a1030"; ctx.textBaseline = "middle";
    ctx.fillText("⚠", px + 14, py + ph / 2 + 1);
    ctx.fillText(text, px + 44, py + ph / 2 + 1);
    const ax = px + pw - 22 + (blink ? 3 : 0);
    ctx.beginPath(); ctx.moveTo(ax - 8, py + 10); ctx.lineTo(ax + 4, py + ph / 2); ctx.lineTo(ax - 8, py + ph - 10); ctx.closePath(); ctx.fill();
    ctx.restore();
  }

  updatePolice() {
    const p = this.player;
    const isCarriage = this.level.hazardSprite === "carriage";
    const isTuktuk = this.level.hazardSprite === "tuktuk";
    const isMaypole = this.level.hazardSprite === "maypole";
    const isMoto = this.level.hazardSprite === "moto";
    const warnText = this.hazardWarnText();
    const warnSfx = (isCarriage || isTuktuk || isMaypole || isMoto) ? "coin" : "siren";
    if (!this.police) {
      this.policeTimer--;
      if (this.policeTimer === 70) { this.sfx(warnSfx); this.popText(this.camera.x + WIDTH - 230, 120, warnText, "#9fdcff"); }
      if (this.policeTimer <= 0) {
        // Per-hazard base speed (slower, playful), then scaled by difficulty
        // (facil 0.85x / normal 1.0x / extremo 1.6x) — applies to every level,
        // including any new ones (unknown sprites fall back to the police speed).
        const HAZARD_BASE = { carriage: 6.6, tuktuk: 5.2, maypole: 4.4, moto: 5.8 };
        const hazBase = HAZARD_BASE[this.level.hazardSprite] || 7.0;
        this.police = { x: this.camera.x + WIDTH + 60, y: GAME.GROUND_Y - 50, w: 116, h: 58, vx: -(hazBase * this.level.diff.policeSpeed) };
        this.sfx(warnSfx);
      }
      return;
    }
    const pol = this.police;
    pol.x += pol.vx;
    const box = { x: pol.x + 10, y: pol.y + 8, w: pol.w - 20, h: pol.h - 8 };
    if (this.overlap(p, box) && p.y + p.h > box.y + 18) this.hurt();
    if (pol.x + pol.w < this.camera.x - 140) {
      this.police = null;
      this.policeTimer = Math.round((380 + Math.random() * 380) * this.level.diff.policeMul);
    }
  }

  updateProjectiles() {
    const p = this.player;
    for (const pr of this.projectiles) {
      pr.x += pr.vx; pr.vy += GRAVITY * 0.6; pr.y += pr.vy; pr.rot += 0.3 * Math.sign(pr.vx);
      if (this.overlap(p, pr)) { pr.dead = true; this.hurt(); }
      if (pr.y > HEIGHT + 40 || pr.x < this.camera.x - 60 || pr.x > this.camera.x + WIDTH + 60) pr.dead = true;
    }
    this.projectiles = this.projectiles.filter((pr) => !pr.dead);

    for (const bt of this.bottles) {
      bt.x += bt.vx; bt.vy += GRAVITY * 0.62; bt.y += bt.vy;
      const hb = { x: bt.x - 6, y: bt.y - 6, w: bt.w + 12, h: bt.h + 12 };
      for (const e of this.level.enemies) if (e.alive && this.overlap(hb, this.enemyBox(e))) { this.throwImpact(bt); this.defeatEnemy(e); bt.dead = true; }
      if (this.boss && this.boss.active && this.boss.alive && this.overlap(hb, { x: this.boss.x, y: this.boss.y, w: this.boss.w, h: this.boss.h })) { this.throwImpact(bt); this.damageBoss(1); bt.dead = true; }
      if (bt.x < this.camera.x - 60 || bt.x > this.camera.x + WIDTH + 60 || bt.y > HEIGHT) bt.dead = true;
    }
    this.bottles = this.bottles.filter((bt) => !bt.dead);
  }

  updateCollectibles() {
    const p = this.player;
    for (const it of this.level.items) {
      if (it.taken) continue;
      const ib = { x: it.x, y: it.y, w: 22, h: 28 };
      if (this.overlap(p, ib)) {
        it.taken = true; this.beers++; this.addScore(100); this.fiesta = Math.min(100, this.fiesta + 14);
        this.sfx(this.level.itemSfx || "beer");
        this.burst(it.x + 9, it.y + 10, ["#ffd24a", "#fff", "#e8b93a"], 12, 5);
        this.popText(it.x - (this.level.itemPop ? 40 : 0), it.y - 6, this.S[this.level.itemPop] || this.S.salud, this.level.itemPop ? "#ff8a3a" : "#ffd24a");
        if (this.fiesta >= 100 && !this.powered) this.activateFiesta();
      }
    }
    // daily treasure chest
    if (this.chest && !this.chest.taken) {
      const cb = { x: this.chest.x, y: this.chest.y, w: 46, h: 44 };
      if (this.overlap(p, cb)) {
        this.chest.taken = true; this.chestOpened = true;
        this.sfx("beer");
        this.burst(this.chest.x + 23, this.chest.y + 10, ["#ffd24a", "#fff", "#ffea70"], 26, 7);
        this.popText(this.chest.x, this.chest.y - 10, "★", "#ffd24a");
        this.cb.onChest && this.cb.onChest();
      }
    }
  }

  updateParticles() {
    for (const pt of this.particles) { pt.x += pt.vx; pt.y += pt.vy; pt.vy += pt.grav; pt.life--; }
    this.particles = this.particles.filter((pt) => pt.life > 0);
    for (const t of this.texts) { t.y += t.vy; t.life--; }
    this.texts = this.texts.filter((t) => t.life > 0);
  }

  // ---------------- Render ----------------
  imgSprite(img, cx, bottomY, targetH, mirror, rot = 0, alpha = 1, sx = 1, sy = 1) {
    const ctx = this.ctx;
    const scale = targetH / img.naturalHeight;
    const w = img.naturalWidth * scale;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(cx, bottomY);
    if (rot) ctx.rotate(rot);
    ctx.scale((mirror ? -1 : 1) * sx, sy);
    ctx.drawImage(img, -w / 2, -targetH, w, targetH);
    ctx.restore();
  }

  drawLevelSun() {
    if (this.level.theme !== "cerronegro") return;
    const ctx = this.ctx;
    const span = Math.max(1, this.level.width - WIDTH);
    const prog = Math.max(0, Math.min(1, this.camera.x / span));
    // Sun follows the runner: starts upper-right, animates left as you progress,
    // arcing gently downward toward the finish.
    const sx = WIDTH * (0.84 - 0.7 * prog);
    const sy = HEIGHT * (0.16 + 0.10 * prog);
    const r = 42;
    const t = this.frame * 0.05;
    // warm glow halo
    const glow = ctx.createRadialGradient(sx, sy, 6, sx, sy, r * 2.8);
    glow.addColorStop(0, "rgba(255,249,224,0.95)");
    glow.addColorStop(0.45, "rgba(255,214,120,0.5)");
    glow.addColorStop(1, "rgba(255,214,120,0)");
    ctx.save();
    ctx.fillStyle = glow;
    ctx.beginPath(); ctx.arc(sx, sy, r * 2.8, 0, Math.PI * 2); ctx.fill();
    // rotating soft rays
    ctx.globalAlpha = 0.35;
    ctx.strokeStyle = "#fff3c4"; ctx.lineWidth = 3;
    for (let i = 0; i < 12; i++) {
      const a = t + (i * Math.PI) / 6;
      ctx.beginPath();
      ctx.moveTo(sx + Math.cos(a) * (r + 8), sy + Math.sin(a) * (r + 8));
      ctx.lineTo(sx + Math.cos(a) * (r + 22), sy + Math.sin(a) * (r + 22));
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    // sun disc
    const disc = ctx.createRadialGradient(sx - r * 0.3, sy - r * 0.3, 4, sx, sy, r);
    disc.addColorStop(0, "#fffdf0");
    disc.addColorStop(1, "#ffd24a");
    ctx.fillStyle = disc;
    ctx.beginPath(); ctx.arc(sx, sy, r, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }

  drawBluefieldsAmbiance() {
    if (this.level.theme !== "bluefields") return;
    const ctx = this.ctx;
    ctx.save();
    // Festive Palo de Mayo ribbons fluttering across the top of the screen
    const cols = ["#ffd400", "#0a47d6", "#e53935", "#2e9e3f", "#ff1493"];
    const t = this.frame * 0.06;
    ctx.lineWidth = 4; ctx.globalAlpha = 0.55;
    for (let i = 0; i < cols.length; i++) {
      const baseY = 26 + i * 12;
      ctx.strokeStyle = cols[i];
      ctx.beginPath();
      for (let x = -20; x <= WIDTH + 20; x += 18) {
        const y = baseY + Math.sin(x * 0.02 + t + i) * 7;
        x === -20 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    // Light Caribbean rain (screen-space) — batched into a single stroke for perf
    ctx.globalAlpha = 0.2; ctx.strokeStyle = "#dff3ff"; ctx.lineWidth = 1.6;
    const rt = this.frame * 9;
    ctx.beginPath();
    for (let i = 0; i < 34; i++) {
      const sx = (i * 137 + rt) % (WIDTH + 80) - 40;
      const sy = (i * 211 + rt * 1.4) % (HEIGHT + 80) - 40;
      ctx.moveTo(sx, sy); ctx.lineTo(sx - 5, sy + 16);
    }
    ctx.stroke();
    ctx.restore();
  }

  drawClouds() {
    const ctx = this.ctx;
    ctx.fillStyle = "#ffffff";
    for (const c of this.clouds) {
      const s = c.s;
      ctx.save();
      ctx.globalAlpha = c.a;
      ctx.beginPath();
      ctx.ellipse(c.x, c.y, 46 * s, 22 * s, 0, 0, Math.PI * 2);
      ctx.ellipse(c.x + 34 * s, c.y + 6 * s, 32 * s, 18 * s, 0, 0, Math.PI * 2);
      ctx.ellipse(c.x - 34 * s, c.y + 8 * s, 26 * s, 15 * s, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  drawLeaves() {
    const ctx = this.ctx;
    for (const lf of this.leaves) {
      ctx.save();
      ctx.globalAlpha = Math.min(0.8, lf.life / 50);
      ctx.translate(lf.x, lf.y);
      ctx.rotate(lf.rot);
      ctx.fillStyle = lf.color;
      ctx.beginPath();
      ctx.ellipse(0, 0, lf.size, lf.size * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  render() {
    const ctx = this.ctx;
    // --- Render interpolation: draw between fixed-timestep states so forward
    // scrolling and jumping look buttery smooth regardless of frame rate.
    const _a = Math.max(0, Math.min(1, this._alpha != null ? this._alpha : 1));
    const _rCamX = this.camera.x, _rPx = this.player.x, _rPy = this.player.y;
    const _interp = !this.ended && !this.paused && this._prevCamX != null;
    if (_interp) {
      this.camera.x = this._prevCamX + (_rCamX - this._prevCamX) * _a;
      this.player.x = this._prevPx + (_rPx - this._prevPx) * _a;
      this.player.y = this._prevPy + (_rPy - this._prevPy) * _a;
    }
    this.adRects = []; // recomputed each frame: clickable sponsor hotspots (start + end billboards)
    ctx.setTransform(this.scale, 0, 0, this.scale, 0, 0); // HiDPI base: logical coords → high-res backing
    ctx.save();
    if (this.shake > 0) ctx.translate((Math.random() - 0.5) * this.shake, (Math.random() - 0.5) * this.shake);

    // Zoom the whole scene in a little (about screen center) so characters,
    // level and hazards all read bigger on screen.
    const ZOOM = 1.18;
    ctx.translate(WIDTH / 2, HEIGHT / 2);
    ctx.scale(ZOOM, ZOOM);
    ctx.translate(-WIDTH / 2, -HEIGHT / 2);

    // Background: immersive non-repeating street segments
    const segKeys = (this.level.bgSegs || []).filter((k) => ready(this.assets[k]));
    if (segKeys.length > 0) this.drawBgSegments(this.camera.x, this.level);
    else {
      const bgImg = this.assets[this.level.bg];
      if (ready(bgImg)) this.drawBgImage(bgImg, this.camera.x);
      else drawBackground(ctx, this.level, this.camera.x, this.frame);
    }

    // Drifting clouds overlay (screen space, above the photo sky)
    this.drawLevelSun();
    this.drawClouds();
    this.drawBluefieldsAmbiance();

    ctx.save();
    ctx.translate(-Math.round(this.camera.x), 0);

    // Sponsorship billboard: shown on every level, styled to fit the theme
    // (Managua standalone billboard, Granada wall-mounted sign, San Juan banner).
    this.drawAdBillboard(ctx);

    for (const pl of this.level.platforms) this.drawPlatform(ctx, pl);
    if (this.level.road) this.drawPits(ctx);
    for (const d of this.level.danceSpots) drawDanceSpot(ctx, d.x, d.y, this.frame);

    // Items (beer, or the level's own collectible sprite e.g. bread for Pan de Vida)
    const beerImg = (this.level.itemSprite && this.assets[this.level.itemSprite]) || this.assets.beer;
    const rot = this.level.itemSprites; // e.g. Pan de Vida rotates bread / pizza / sushi
    let itemIdx = 0;
    for (const it of this.level.items) {
      const k = itemIdx++;
      if (it.taken) continue;
      const bob = Math.sin(this.frame * 0.1) * 3;
      const img = rot ? (this.assets[rot[k % rot.length]] || beerImg) : beerImg;
      if (ready(img)) this.imgSprite(img, it.x + 11, it.y + 30 + bob, 34, false);
      else drawBeer(ctx, it.x, it.y, this.frame);
    }

    // Daily treasure chest (drawn with a glow + floating arrow so players spot it)
    if (this.chest && !this.chest.taken) this.drawChest(ctx, this.chest.x, this.chest.y);

    // Goal pinata (non-boss levels)
    if (!this.boss) {
      const pin = this.assets.pinata;
      if (ready(pin)) {
        const sway = Math.sin(this.frame * 0.08) * 8;
        ctx.fillStyle = "#7a5a2a"; ctx.fillRect(this.level.goalX + 16, GAME.GROUND_Y - 250, 6, 250);
        this.imgSprite(pin, this.level.goalX + 20 + sway, GAME.GROUND_Y - 150, 120, false);
      } else drawGoal(ctx, this.level.goalX, GAME.GROUND_Y, this.frame);
    }

    // Enemies
    const dogImg = this.assets.dog, abImg = (this.level.abuelaSprite && this.assets[this.level.abuelaSprite]) || this.assets.abuela;
    for (const e of this.level.enemies) {
      if (!e.alive) continue;
      if (e.type === "dog") {
        if (ready(dogImg)) {
          if (e.charger) { ctx.filter = e.charging ? "saturate(2) hue-rotate(-30deg) brightness(1.1)" : "saturate(1.5) hue-rotate(-20deg)"; }
          this.imgSprite(dogImg, e.x + 19, e.y + 26, e.charger ? 62 : 54, e.dir < 0);
          ctx.filter = "none";
        } else drawDog(ctx, e.x, e.y, this.frame, e.dir);
      } else {
        const faceLeft = this.player.x < e.x;
        if (ready(abImg)) this.imgSprite(abImg, e.x + 15, e.y + 50, 88, faceLeft);
        else drawAbuela(ctx, e.x, e.y, this.frame);
      }
    }

    // Boss
    const b = this.boss;
    if (b && b.alive) {
      const bossImg = this.assets.boss;
      const flash = b.hurtT > 0 && Math.floor(this.frame / 3) % 2 === 0;
      const faceRight = this.player.x > b.x; // sprite natural faces LEFT
      if (ready(bossImg)) this.imgSprite(bossImg, b.x + b.w / 2, b.y + b.h, b.h + 50, faceRight, 0, flash ? 0.4 : 1);
      else { ctx.fillStyle = flash ? "#fff" : "#7a3d8a"; ctx.fillRect(b.x, b.y, b.w, b.h); }
    } else if (b && b.defeated) {
      this.drawBossDefeatMain(b);
    }

    // Flying hazard (police car, or per-level override e.g. Granada horse carriage)
    if (this.police) {
      const pol = this.assets[this.level.hazardSprite] || this.assets.police;
      const sizeMul = (this.level.hazardSprite === "carriage" || this.level.hazardSprite === "tuktuk" || this.level.hazardSprite === "maypole") ? 78 : this.level.hazardSprite === "moto" ? 42 : 34;
      if (ready(pol)) this.imgSprite(pol, this.police.x + this.police.w / 2, this.police.y + this.police.h, this.police.h + sizeMul, false);
      else { ctx.fillStyle = "#1a3a8a"; ctx.fillRect(this.police.x, this.police.y, this.police.w, this.police.h); ctx.fillStyle = "#e23"; ctx.fillRect(this.police.x + 40, this.police.y - 8, 36, 8); }
    }

    // Projectiles
    const chImg = this.assets.chancleta;
    for (const pr of this.projectiles) {
      if (ready(chImg)) this.imgSprite(chImg, pr.x + 7, pr.y + 8, 26, pr.vx < 0, pr.rot);
      else drawChancleta(ctx, pr.x, pr.y, pr.rot);
    }
    for (const bt of this.bottles) {
      if (bt.kind === "bread") {
        bt.rot = (bt.rot || 0) + 0.16 * Math.sign(bt.vx);
        ctx.save(); ctx.translate(bt.x + 9, bt.y + 8); ctx.rotate(bt.rot);
        ctx.fillStyle = "#b9772e"; ctx.beginPath(); ctx.ellipse(0, 1, 12, 7, 0, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#d9a441"; ctx.beginPath(); ctx.ellipse(-1, 0, 10, 5.5, 0, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = "#8a5a22"; ctx.lineWidth = 1.4; ctx.lineCap = "round";
        for (let i = -1; i <= 1; i++) { ctx.beginPath(); ctx.moveTo(i * 5 - 2, -4); ctx.lineTo(i * 5 + 2, 4); ctx.stroke(); }
        ctx.restore();
      } else if (bt.kind === "cream") {
        const dir = Math.sign(bt.vx) || 1;
        ctx.save(); ctx.translate(bt.x + 9, bt.y + 8);
        ctx.fillStyle = "rgba(255,250,235,0.55)";
        ctx.beginPath(); ctx.ellipse(-dir * 9, 0, 7, 3, 0, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#fffdf5";
        ctx.beginPath(); ctx.arc(0, 0, 7, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#fff3c4";
        ctx.beginPath(); ctx.arc(dir * 2, -2, 3.2, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = "#e8d9a8"; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.arc(0, 0, 7, 0, Math.PI * 2); ctx.stroke();
        ctx.restore();
      } else if (bt.kind === "starnote") {
        bt.rot += 0.18;
        ctx.save(); ctx.translate(bt.x + 9, bt.y + 8); ctx.rotate(bt.rot);
        // gold 5-point star
        ctx.fillStyle = "#ffd24a"; ctx.strokeStyle = "#b9860b"; ctx.lineWidth = 1.4;
        ctx.beginPath();
        for (let i = 0; i < 5; i++) {
          const a1 = -Math.PI / 2 + i * (Math.PI * 2 / 5);
          const a2 = a1 + Math.PI / 5;
          ctx.lineTo(Math.cos(a1) * 9, Math.sin(a1) * 9);
          ctx.lineTo(Math.cos(a2) * 4, Math.sin(a2) * 4);
        }
        ctx.closePath(); ctx.fill(); ctx.stroke();
        ctx.restore();
        // trailing music note
        ctx.save(); ctx.translate(bt.x - 6, bt.y + 4); ctx.fillStyle = "#7e57c2";
        ctx.beginPath(); ctx.arc(0, 4, 3, 0, Math.PI * 2); ctx.fill();
        ctx.fillRect(2.4, -6, 1.6, 10);
        ctx.fillRect(2.4, -6, 5, 2.4);
        ctx.restore();
      } else if (bt.kind === "hypno") {
        const dir = Math.sign(bt.vx) || 1;
        bt.rot += 0.35;
        ctx.save(); ctx.translate(bt.x + 9, bt.y + 8);
        // hypnotic magic swirl trail
        ctx.save(); ctx.rotate(bt.rot);
        ctx.strokeStyle = "rgba(196,77,255,0.9)"; ctx.lineWidth = 2.2; ctx.lineCap = "round";
        ctx.beginPath();
        for (let a = 0; a < Math.PI * 4; a += 0.3) {
          const r = 2 + a * 1.6;
          const px = Math.cos(a) * r, py = Math.sin(a) * r;
          a === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
        }
        ctx.stroke();
        ctx.strokeStyle = "rgba(255,119,224,0.7)"; ctx.beginPath();
        for (let a = 0; a < Math.PI * 3.4; a += 0.3) {
          const r = 4 + a * 1.4;
          const px = Math.cos(a + Math.PI) * r, py = Math.sin(a + Math.PI) * r;
          a === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
        }
        ctx.stroke();
        ctx.restore();
        // boxing glove / fist punching forward
        ctx.scale(dir, 1);
        ctx.fillStyle = "#e2295b"; ctx.strokeStyle = "#7a0f2b"; ctx.lineWidth = 1.6;
        ctx.beginPath(); ctx.ellipse(3, 0, 9, 7, 0, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
        ctx.fillStyle = "#ff5c85";
        ctx.beginPath(); ctx.ellipse(1, -2, 4, 3, 0, 0, Math.PI * 2); ctx.fill();
        // wrist band (Nicaragua blue)
        ctx.fillStyle = "#0a47d6"; ctx.fillRect(-10, -5, 4, 10);
        ctx.restore();
      } else if (bt.kind === "tornado") {
        const dir = Math.sign(bt.vx) || 1;
        bt.rot += 0.5;
        ctx.save(); ctx.translate(bt.x + 9, bt.y + 8); ctx.scale(dir, 1);
        // swirling funnel
        ctx.strokeStyle = "rgba(207,232,255,0.95)"; ctx.lineWidth = 2.4; ctx.lineCap = "round";
        for (let i = 0; i < 4; i++) {
          const yy = -10 + i * 6;
          const rw = 11 - i * 2.2;
          const ph = bt.rot + i * 0.9;
          ctx.beginPath();
          ctx.ellipse(Math.sin(ph) * 2, yy, rw, 2.4, 0, 0, Math.PI * 2);
          ctx.stroke();
        }
        ctx.fillStyle = "rgba(159,196,232,0.35)";
        ctx.beginPath(); ctx.moveTo(-12, -12); ctx.lineTo(12, -12); ctx.lineTo(3, 8); ctx.lineTo(-3, 8); ctx.closePath(); ctx.fill();
        ctx.restore();
      } else if (bt.kind === "rock") {
        bt.rot = (bt.rot || 0) + 0.25 * Math.sign(bt.vx);
        ctx.save(); ctx.translate(bt.x + 7, bt.y + 7); ctx.rotate(bt.rot);
        ctx.fillStyle = "#6f6f6f"; ctx.beginPath(); ctx.arc(0, 0, 8, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#9e9e9e"; ctx.beginPath(); ctx.arc(-2, -2, 4, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
      } else if (bt.kind === "ball") {
        bt.rot = (bt.rot || 0) + 0.3 * Math.sign(bt.vx);
        ctx.save(); ctx.translate(bt.x + 8, bt.y + 8); ctx.rotate(bt.rot);
        const R = 9;
        // shaded white leather sphere
        const g = ctx.createRadialGradient(-R * 0.4, -R * 0.4, R * 0.15, 0, 0, R);
        g.addColorStop(0, "#ffffff"); g.addColorStop(0.72, "#eef0f2"); g.addColorStop(1, "#c3c7cd");
        ctx.fillStyle = g; ctx.beginPath(); ctx.arc(0, 0, R, 0, Math.PI * 2); ctx.fill();
        const pent = (cx, cy, rad, rot0) => {
          ctx.beginPath();
          for (let k = 0; k < 5; k++) { const a = rot0 + k * Math.PI * 2 / 5 - Math.PI / 2; const x = cx + Math.cos(a) * rad, y = cy + Math.sin(a) * rad; k ? ctx.lineTo(x, y) : ctx.moveTo(x, y); }
          ctx.closePath();
        };
        // black pentagons: center + surrounding ring
        ctx.fillStyle = "#191919";
        pent(0, 0, 3.3, 0); ctx.fill();
        for (let i = 0; i < 5; i++) { const a = i * Math.PI * 2 / 5 - Math.PI / 2; pent(Math.cos(a) * 6.4, Math.sin(a) * 6.4, 2.0, a + Math.PI); ctx.fill(); }
        // stitched seams from center toward each outer pentagon
        ctx.strokeStyle = "rgba(0,0,0,0.4)"; ctx.lineWidth = 0.8;
        for (let i = 0; i < 5; i++) { const a = i * Math.PI * 2 / 5 - Math.PI / 2; ctx.beginPath(); ctx.moveTo(Math.cos(a) * 2.7, Math.sin(a) * 2.7); ctx.lineTo(Math.cos(a) * 6.4, Math.sin(a) * 6.4); ctx.stroke(); }
        // rim + glossy highlight
        ctx.strokeStyle = "#3a3a3a"; ctx.lineWidth = 1.1; ctx.beginPath(); ctx.arc(0, 0, R, 0, Math.PI * 2); ctx.stroke();
        ctx.fillStyle = "rgba(255,255,255,0.6)"; ctx.beginPath(); ctx.ellipse(-R * 0.42, -R * 0.42, 2.2, 1.5, -0.7, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
      } else if (bt.kind === "baseball") {
        bt.rot = (bt.rot || 0) + 0.35 * Math.sign(bt.vx);
        ctx.save(); ctx.translate(bt.x + 9, bt.y + 9); ctx.rotate(bt.rot);
        const R = 9;
        const g = ctx.createRadialGradient(-R * 0.4, -R * 0.4, R * 0.15, 0, 0, R);
        g.addColorStop(0, "#ffffff"); g.addColorStop(0.8, "#f4f1ea"); g.addColorStop(1, "#d9d2c4");
        ctx.fillStyle = g; ctx.beginPath(); ctx.arc(0, 0, R, 0, Math.PI * 2); ctx.fill();
        // red curved stitching on both sides
        ctx.strokeStyle = "#d0202f"; ctx.lineWidth = 1.3;
        for (const s of [-1, 1]) {
          ctx.beginPath(); ctx.arc(s * R * 1.35, 0, R * 1.05, s > 0 ? Math.PI * 0.68 : -Math.PI * 0.32, s > 0 ? Math.PI * 1.32 : Math.PI * 0.32); ctx.stroke();
          for (let k = -3; k <= 3; k++) { const a = (s > 0 ? Math.PI : 0) + k * 0.14; const cx = s * R * 1.35 + Math.cos(a) * R * 1.05, cy = Math.sin(a) * R * 1.05; ctx.beginPath(); ctx.moveTo(cx - 1.2, cy - 1.2); ctx.lineTo(cx + 1.2, cy + 1.2); ctx.stroke(); }
        }
        ctx.strokeStyle = "#b8b1a3"; ctx.lineWidth = 0.9; ctx.beginPath(); ctx.arc(0, 0, R, 0, Math.PI * 2); ctx.stroke();
        ctx.fillStyle = "rgba(255,255,255,0.6)"; ctx.beginPath(); ctx.ellipse(-R * 0.42, -R * 0.42, 2, 1.4, -0.7, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
      } else if (bt.kind === "nacatamal") {
        bt.rot = (bt.rot || 0) + 0.18 * Math.sign(bt.vx);
        ctx.save(); ctx.translate(bt.x + 9, bt.y + 8); ctx.rotate(bt.rot);
        ctx.fillStyle = "#2e5d1f"; ctx.beginPath(); ctx.roundRect ? ctx.roundRect(-11, -8, 22, 16, 4) : ctx.rect(-11, -8, 22, 16); ctx.fill();
        ctx.fillStyle = "#4a8a2c"; ctx.beginPath(); ctx.roundRect ? ctx.roundRect(-9, -6, 18, 12, 3) : ctx.rect(-9, -6, 18, 12); ctx.fill();
        ctx.strokeStyle = "#caa84a"; ctx.lineWidth = 1.6;
        ctx.beginPath(); ctx.moveTo(-3, -7); ctx.lineTo(-3, 7); ctx.moveTo(4, -7); ctx.lineTo(4, 7); ctx.stroke();
        ctx.restore();
      } else if (bt.kind === "fritanga") {
        bt.rot = (bt.rot || 0) + 0.2 * Math.sign(bt.vx);
        ctx.save(); ctx.translate(bt.x + 9, bt.y + 8); ctx.rotate(bt.rot);
        ctx.fillStyle = "#2e7d32"; ctx.beginPath(); ctx.ellipse(0, 3, 11, 6, 0, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#c97b2a"; ctx.beginPath(); ctx.ellipse(-2, 0, 7, 5, 0, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#f0a830"; ctx.beginPath(); ctx.ellipse(3, -1, 4, 3, 0.5, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#e53935"; ctx.beginPath(); ctx.arc(-4, -1, 1.6, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
      } else if (bt.kind === "coconut") {
        bt.rot = (bt.rot || 0) + 0.22 * Math.sign(bt.vx);
        ctx.save(); ctx.translate(bt.x + 9, bt.y + 8); ctx.rotate(bt.rot);
        ctx.fillStyle = "#6b4423"; ctx.beginPath(); ctx.arc(0, 0, 9, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#8a5a2b"; ctx.beginPath(); ctx.arc(-2, -2, 6, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#7a4e26"; ctx.lineWidth = 1; ctx.strokeStyle = "#5a3a1e";
        ctx.beginPath(); ctx.moveTo(-7, 0); ctx.quadraticCurveTo(0, 2, 7, 0); ctx.stroke();
        ctx.fillStyle = "#3a2614";
        ctx.beginPath(); ctx.arc(3, -3, 1.4, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(5, 1, 1.4, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(1, 1, 1.4, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
      } else if (bt.kind === "note") {
        bt.rot = (bt.rot || 0) + 0.12 * Math.sign(bt.vx);
        ctx.save(); ctx.translate(bt.x + 8, bt.y + 8); ctx.rotate(Math.sin(bt.rot) * 0.3);
        ctx.font = "800 26px 'Baloo 2', system-ui, sans-serif";
        ctx.fillStyle = "#000"; ctx.fillText("♪", -8, 9);
        ctx.fillStyle = "#7e57c2"; ctx.fillText("♪", -9, 8);
        ctx.restore();
      } else if (bt.kind === "maraca") {
        bt.rot = (bt.rot || 0) + 0.4 * (Math.sign(bt.vx) || 1);
        ctx.save();
        ctx.translate(bt.x + 8, bt.y + 8);
        ctx.rotate(bt.rot);
        ctx.strokeStyle = "#6d4c2f"; ctx.lineWidth = 4; ctx.lineCap = "round";
        ctx.beginPath(); ctx.moveTo(0, 4); ctx.lineTo(0, 15); ctx.stroke();
        ctx.fillStyle = "#e0a82e";
        ctx.beginPath(); ctx.ellipse(0, -4, 8, 9, 0, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#b8860b";
        ctx.beginPath(); ctx.ellipse(0, -1, 8, 5, 0, 0, Math.PI); ctx.fill();
        ctx.fillStyle = "#4a2f12";
        ctx.beginPath(); ctx.arc(-2, -6, 1.3, 0, Math.PI * 2); ctx.arc(3, -3, 1.3, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
      } else if (bt.kind === "bolt") {
        const dir = Math.sign(bt.vx) || 1;
        bt.rot = (bt.rot || 0) + 1;
        ctx.save();
        ctx.translate(bt.x + 8, bt.y + 8);
        ctx.scale(dir, 1);
        // outer glow
        ctx.shadowColor = "#00d8ff"; ctx.shadowBlur = 14;
        // jagged lightning bolt
        const seed = bt.rot;
        ctx.lineCap = "round"; ctx.lineJoin = "round";
        const drawBolt = (color, lw) => {
          ctx.strokeStyle = color; ctx.lineWidth = lw;
          ctx.beginPath();
          ctx.moveTo(-14, 0);
          ctx.lineTo(-4, -5 + Math.sin(seed) * 2);
          ctx.lineTo(0, 1);
          ctx.lineTo(7, -4 + Math.cos(seed) * 2);
          ctx.lineTo(14, 2);
          ctx.stroke();
        };
        drawBolt("#a8f0ff", 6);
        drawBolt("#00d8ff", 3.2);
        drawBolt("#ffffff", 1.4);
        // electric core orb
        ctx.shadowBlur = 18;
        ctx.fillStyle = "#ffffff";
        ctx.beginPath(); ctx.arc(0, 0, 3.4, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "rgba(0,216,255,0.6)";
        ctx.beginPath(); ctx.arc(0, 0, 6, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
      } else if (ready(beerImg)) this.imgSprite(beerImg, bt.x + 7, bt.y + 18, 26, bt.vx < 0);
      else drawBottle(ctx, bt.x, bt.y);
    }

    // Player
    const p = this.player;
    const pImg = this.assets[this.char.spriteId || this.char.id];
    if (!(this.invincible > 0 && Math.floor(this.frame / 4) % 2 === 0)) {
      if (ready(pImg)) {
        let bob = 0, rot = 0, sx = 1, sy = 1;
        if (p.state === "walk") {
          const t = p.animFrame * 0.32;
          bob = -Math.abs(Math.sin(t)) * 6;
          rot = p.facing * 0.06 + Math.sin(t) * 0.04; // forward lean + stride sway
          sy = 1 + Math.sin(t) * 0.05;
          sx = 1 - Math.sin(t) * 0.035;
        } else if (p.state === "jump") { rot = 0.1 * p.facing; sy = 1.07; sx = 0.95; }
        else if (p.state === "dance") rot = Math.sin(p.animFrame * 0.5) * 0.2;
        if (p.spinTimer > 0) { p.spinTimer--; rot = (20 - p.spinTimer) * 0.9 * p.facing; }
        // Special Guest Fiesta aura: pulsing ability-themed glow + orbiting effects while powered.
        if (this.powered && this.char.guest) {
          const cx = p.x + p.w / 2, cy = p.y + p.h / 2 + bob * 0.4;
          const pulse = 0.5 + 0.5 * Math.sin(this.frame * 0.12);
          const R = 64 + pulse * 16;
          const ab = this.char.ability;
          const GLOW = { starnote: [255, 210, 74], tornado: [180, 210, 240], electric: [0, 216, 255], nacatamal: [124, 179, 66], coconut: [0, 242, 234], notes: [126, 87, 194], ball: [255, 212, 0], fritanga: [255, 120, 30], hypno: [196, 77, 255] };
          const DOT = { starnote: "#fff3c4", tornado: "#eaf4ff", electric: "#bff4ff", nacatamal: "#dff0bf", coconut: "#c8fff9", notes: "#e5dcff", ball: "#ffffff", fritanga: "#ffd8a0", hypno: "#ff77e0" };
          const rgb = GLOW[ab] || [255, 210, 74];
          const dotCol = DOT[ab] || "#fff3c4";
          ctx.save();
          const g = ctx.createRadialGradient(cx, cy, 6, cx, cy, R);
          g.addColorStop(0, `rgba(${rgb[0]},${rgb[1]},${rgb[2]},0.42)`);
          g.addColorStop(0.55, `rgba(${rgb[0]},${rgb[1]},${rgb[2]},0.20)`);
          g.addColorStop(1, `rgba(${rgb[0]},${rgb[1]},${rgb[2]},0)`);
          ctx.fillStyle = g;
          ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.fill();
          if (ab === "hypno") {
            // signature concentric hypnotic rings spinning around her
            ctx.save(); ctx.translate(cx, cy); ctx.rotate(this.frame * 0.06);
            ctx.strokeStyle = "rgba(255,119,224,0.85)"; ctx.lineWidth = 2.8;
            ctx.shadowColor = "rgba(196,77,255,0.9)"; ctx.shadowBlur = 8;
            for (let r = 14; r < R * 0.9; r += 11) {
              ctx.globalAlpha = 0.3 + 0.5 * pulse;
              ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 1.4); ctx.stroke();
            }
            ctx.globalAlpha = 1; ctx.restore();
          }
          for (let i = 0; i < 3; i++) {
            const a = this.frame * 0.05 + i * (Math.PI * 2 / 3);
            const ox = cx + Math.cos(a) * (R * 0.78), oy = cy + Math.sin(a) * (R * 0.5);
            ctx.shadowColor = `rgba(${rgb[0]},${rgb[1]},${rgb[2]},0.9)`; ctx.shadowBlur = 10;
            if (ab === "starnote" || ab === "notes") {
              // orbiting 5-point star
              const sr = 3.4 + pulse * 2;
              ctx.fillStyle = dotCol; ctx.beginPath();
              for (let k = 0; k < 5; k++) {
                const a1 = -Math.PI / 2 + k * (Math.PI * 2 / 5), a2 = a1 + Math.PI / 5;
                ctx.lineTo(ox + Math.cos(a1) * sr, oy + Math.sin(a1) * sr);
                ctx.lineTo(ox + Math.cos(a2) * sr * 0.45, oy + Math.sin(a2) * sr * 0.45);
              }
              ctx.closePath(); ctx.fill();
            } else if (ab === "tornado") {
              // orbiting mini tornado funnel
              ctx.save(); ctx.translate(ox, oy); ctx.rotate(this.frame * 0.3 + i);
              ctx.strokeStyle = dotCol; ctx.lineWidth = 1.8; ctx.lineCap = "round";
              for (let s = 0; s < 3; s++) {
                const yy = -5 + s * 4, rw = 5.5 - s * 1.6;
                ctx.beginPath(); ctx.ellipse(0, yy, rw, 1.8, 0, 0, Math.PI * 2); ctx.stroke();
              }
              ctx.restore();
            } else if (ab === "electric") {
              // jagged electric spark
              ctx.strokeStyle = dotCol; ctx.lineWidth = 2; ctx.lineCap = "round";
              ctx.beginPath();
              ctx.moveTo(ox - 4, oy - 5); ctx.lineTo(ox + 1, oy - 1); ctx.lineTo(ox - 2, oy + 1); ctx.lineTo(ox + 4, oy + 5);
              ctx.stroke();
            } else if (ab === "hypno") {
              // orbiting hypnotic spiral ring
              ctx.save(); ctx.translate(ox, oy); ctx.rotate(this.frame * 0.25 + i);
              ctx.strokeStyle = dotCol; ctx.lineWidth = 1.6; ctx.lineCap = "round";
              ctx.beginPath();
              for (let t = 0; t < Math.PI * 3.2; t += 0.35) {
                const rr = 1 + t * 0.9, px = Math.cos(t) * rr, py = Math.sin(t) * rr;
                t === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
              }
              ctx.stroke();
              ctx.restore();
            } else {
              // glowing orb (fritanga / coconut / nacatamal / ball / others)
              const rr = 3 + pulse * 1.8;
              ctx.fillStyle = dotCol; ctx.beginPath(); ctx.arc(ox, oy, rr, 0, Math.PI * 2); ctx.fill();
            }
          }
          ctx.restore();
        }
        if (this.powered) { ctx.save(); ctx.shadowColor = "#ffd400"; ctx.shadowBlur = 22; }
        this.imgSprite(pImg, p.x + p.w / 2, p.y + p.h + bob, p.h * (this.char.spriteScale || 1.7), p.facing < 0, rot, 1, sx, sy);
        if (this.powered) ctx.restore();
        // Signature label floating above the character while the special (FIESTA) is active.
        if (this.powered && this.char.powText) {
          const ty = p.y + bob - 22 - Math.sin(this.frame * 0.15) * 4;
          ctx.save();
          ctx.textAlign = "center"; ctx.textBaseline = "bottom";
          ctx.font = "800 17px 'Baloo 2', system-ui, sans-serif";
          ctx.lineJoin = "round"; ctx.lineWidth = 5; ctx.strokeStyle = "rgba(0,0,0,0.7)";
          ctx.strokeText(this.char.powText, p.x + p.w / 2, ty);
          ctx.shadowColor = "rgba(255,122,24,0.9)"; ctx.shadowBlur = 10;
          ctx.fillStyle = "#ffb300";
          ctx.fillText(this.char.powText, p.x + p.w / 2, ty);
          ctx.restore();
        }
      } else {
        drawCharacter(ctx, this.char, p.x, p.y, p.w, p.h, { frame: p.animFrame, facing: p.facing, state: p.state, powered: this.powered });
      }
    }

    // Particles
    for (const pt of this.particles) {
      if (pt.note) { ctx.fillStyle = pt.color; ctx.font = "700 20px 'Baloo 2', system-ui, sans-serif"; ctx.globalAlpha = Math.min(1, pt.life / 20); ctx.fillText("♪", pt.x, pt.y); ctx.globalAlpha = 1; }
      else { ctx.globalAlpha = Math.min(1, pt.life / 18); ctx.fillStyle = pt.color; ctx.fillRect(Math.round(pt.x), Math.round(pt.y), pt.size, pt.size); ctx.globalAlpha = 1; }
    }
    for (const t of this.texts) {
      ctx.globalAlpha = Math.min(1, t.life / 20); ctx.font = "800 18px 'Baloo 2', system-ui, sans-serif";
      ctx.fillStyle = "#000"; ctx.fillText(t.text, t.x + 1, t.y + 1);
      ctx.fillStyle = t.color; ctx.fillText(t.text, t.x, t.y); ctx.globalAlpha = 1;
    }

    ctx.restore();
    // Wind-blown leaves in the foreground (screen space)
    this.drawLeaves();
    ctx.restore();
    this.drawHazardWarning();
    if (_interp) { this.camera.x = _rCamX; this.player.x = _rPx; this.player.y = _rPy; }
  }

  // Draw pit gaps as clearly-visible dark holes with bright warning lips, so
  // players can plainly see where the road/ground drops away.
  drawPits(ctx) {
    const y = GAME.GROUND_Y;
    for (const pit of this.level.pits) {
      if (pit.x + pit.w < this.camera.x - 60 || pit.x > this.camera.x + WIDTH + 60) continue;
      // Recessed DIRT hole (not a black void): earthy tones so it reads as a gap
      // in the dirt road you can clearly see into.
      ctx.fillStyle = "#5b4227"; ctx.fillRect(pit.x, y, pit.w, 240);
      ctx.fillStyle = "#6e5230"; ctx.fillRect(pit.x, y, pit.w, 8); // sunlit near lip
      const g = ctx.createLinearGradient(0, y, 0, y + 110);
      g.addColorStop(0, "rgba(40,26,12,0.85)");
      g.addColorStop(1, "rgba(60,42,24,0.25)");
      ctx.fillStyle = g; ctx.fillRect(pit.x, y, pit.w, 110);
      // darker crumbly dirt walls on the sides
      ctx.fillStyle = "rgba(35,22,10,0.55)";
      ctx.fillRect(pit.x, y, 10, 200);
      ctx.fillRect(pit.x + pit.w - 10, y, 10, 200);
      // soft road-edge lip highlight so the opening is obvious
      ctx.fillStyle = "rgba(210,175,120,0.9)";
      ctx.fillRect(pit.x - 6, y - 3, 6, 8);
      ctx.fillRect(pit.x + pit.w, y - 3, 6, 8);
    }
  }


  // Zoomed-out background for featured photo levels: fit the photo to the full
  // segment WIDTH and rest its foreground right on the ground line so the scene
  // flows into the road (instead of a tightly-cropped cover fill).
  drawSegBg(img, dx, segW) {
    const ctx = this.ctx;
    const bottom = GAME.GROUND_Y + 2; // full photo visible, foreground rests on the road line
    // Darkened cover backdrop fills the tile width so there are no empty side bars.
    this._coverRect(ctx, img, dx, 0, segW, bottom);
    ctx.fillStyle = "rgba(18,22,38,0.32)";
    ctx.fillRect(dx, 0, segW, bottom);
    // Whole photo fit by HEIGHT and centred — zoomed back so the full scene
    // (e.g. the entire Toña bottle) is visible above the road.
    const scale = bottom / img.naturalHeight;
    const iw = img.naturalWidth * scale;
    const ix = dx + (segW - iw) / 2;
    ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight, ix, 0, iw, bottom);
    // Dim the side margins into a vignette so the backdrop never reads as a
    // faint duplicate of the photo at the edges.
    const m = (segW - iw) / 2;
    if (m > 1) {
      let g = ctx.createLinearGradient(dx, 0, dx + m, 0);
      g.addColorStop(0, "rgba(10,12,22,0.82)"); g.addColorStop(1, "rgba(10,12,22,0)");
      ctx.fillStyle = g; ctx.fillRect(dx, 0, m + 1, bottom);
      g = ctx.createLinearGradient(dx + segW - m, 0, dx + segW, 0);
      g.addColorStop(0, "rgba(10,12,22,0)"); g.addColorStop(1, "rgba(10,12,22,0.82)");
      ctx.fillStyle = g; ctx.fillRect(dx + segW - m - 1, 0, m + 1, bottom);
    }
  }

  _coverRect(ctx, img, dx, dy, dw, dh) {
    const dr = dw / dh, ir = img.naturalWidth / img.naturalHeight;
    let sw, sh, sx, sy;
    if (ir > dr) { sh = img.naturalHeight; sw = sh * dr; sx = (img.naturalWidth - sw) / 2; sy = 0; }
    else { sw = img.naturalWidth; sh = sw / dr; sx = 0; sy = (img.naturalHeight - sh) / 2; }
    ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
  }

  drawImageCover(img, dx, dy, dw, dh) {
    const ctx = this.ctx;
    const ir = img.naturalWidth / img.naturalHeight;
    const dr = dw / dh;
    let sw, sh, sx, sy;
    if (ir > dr) { sh = img.naturalHeight; sw = sh * dr; sx = (img.naturalWidth - sw) / 2; sy = 0; }
    else { sw = img.naturalWidth; sh = sw / dr; sx = 0; sy = (img.naturalHeight - sh) / 2; }
    ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
  }

  // Bilingual "ADS HERE" sponsorship billboard — styled per level theme:
  //  • Managua (0): free-standing billboard on two posts
  //  • Granada (1): framed sign mounted on the side of a building/church wall
  //  • San Juan del Sur (2): cloth banner strung across two light poles
  drawAdBillboard(ctx) {
    const groundY = GAME.GROUND_Y;
    const idx = this.level.index;
    const pw = 250, ph = 132;
    let pyTop = groundY - 318;
    let startX = 1500;

    // Two billboards per level: one near the start, one near the end (before the goal).
    const goalX = this.level.goalX || (this.level.width - 200);
    const endX = Math.min(this.level.width - pw - 40, Math.max(startX + 760, goalX - 560));
    this.adRects = [];
    this._drawBillboardAt(ctx, idx, startX, pyTop, pw, ph, groundY, "start");
    if (endX > startX + pw + 60) this._drawBillboardAt(ctx, idx, endX, pyTop, pw, ph, groundY, "end");
  }

  _drawBillboardAt(ctx, idx, px, pyTop, pw, ph, groundY, slot) {
    const ad = this.sponsorAds[slot] || null;
    const m = ctx.getTransform();
    const sx = m.a * px + m.e, sw = m.a * pw;
    // Keep ad videos playing continuously from the start (looping), so by the time
    // the player runs up to a billboard it's already mid-playback — only the game
    // being paused/ended stops them.
    if (ad && ad.video) {
      if (!this.paused && !this.ended) { if (ad.video.paused) ad.video.play().catch(() => {}); }
    }
    // Clickable hotspot: a live sponsor ad (with a link) opens it & tracks the
    // click; the default "ADS HERE" placeholder shares the app.
    const clickable = (ad && ad.link) || !ad;
    if (clickable) {
      this.adRects.push({ x: sx, y: m.d * pyTop + m.f, w: sw, h: m.d * ph, slot });
    }

    if (idx === 999) this.drawAdFrameGranada(ctx, px, pyTop, pw, ph, groundY);
    else this.drawAdFrameSanJuan(ctx, px, pyTop, pw, ph, groundY);

    this.drawAdPanelContent(ctx, px, pyTop, pw, ph, ad);
  }

  // Managua: free-standing billboard supported by two posts.
  drawAdFrameManagua(ctx, px, pyTop, pw, ph, groundY) {
    ctx.fillStyle = "#33333b";
    ctx.fillRect(px + 26, pyTop + ph, 16, groundY - (pyTop + ph));
    ctx.fillRect(px + pw - 42, pyTop + ph, 16, groundY - (pyTop + ph));
  }

  // Granada: framed sign bolted to a warm colonial stucco wall (building/church side).
  drawAdFrameGranada(ctx, px, pyTop, pw, ph, groundY) {
    const pad = 26;
    const wx = px - pad, wy = pyTop - pad - 18, ww = pw + pad * 2, wh = ph + pad * 2 + 18;
    // drop shadow (mounted-on-wall feel)
    ctx.fillStyle = "rgba(0,0,0,0.28)";
    ctx.fillRect(wx + 8, wy + 10, ww, wh);
    // stucco wall plaque
    const g = ctx.createLinearGradient(wx, wy, wx, wy + wh);
    g.addColorStop(0, "#f0c451"); g.addColorStop(1, "#e0a534");
    ctx.fillStyle = g; ctx.fillRect(wx, wy, ww, wh);
    // colonial trim border
    ctx.lineWidth = 6; ctx.strokeStyle = "#8a5220";
    ctx.strokeRect(wx + 3, wy + 3, ww - 6, wh - 6);
    // little arched cornice at top (church/colonial facade hint)
    ctx.fillStyle = "#c8842a";
    ctx.beginPath();
    ctx.moveTo(wx, wy); ctx.lineTo(wx + ww, wy);
    ctx.lineTo(wx + ww, wy - 10);
    ctx.quadraticCurveTo(wx + ww / 2, wy - 30, wx, wy - 10);
    ctx.closePath(); ctx.fill();
    // corner mounting bolts
    ctx.fillStyle = "#5a3210";
    const bolt = (bx, by) => { ctx.beginPath(); ctx.arc(bx, by, 5, 0, Math.PI * 2); ctx.fill(); };
    bolt(wx + 12, wy + 12); bolt(wx + ww - 12, wy + 12);
    bolt(wx + 12, wy + wh - 12); bolt(wx + ww - 12, wy + wh - 12);
  }

  // San Juan del Sur: cloth banner strung between two beach light poles.
  drawAdFrameSanJuan(ctx, px, pyTop, pw, ph, groundY) {
    const lx = px - 56, rx = px + pw + 56; // pole x positions
    const topY = pyTop - 46;
    // poles
    ctx.fillStyle = "#3b4046";
    ctx.fillRect(lx - 6, topY, 12, groundY - topY);
    ctx.fillRect(rx - 6, topY, 12, groundY - topY);
    // pole bases
    ctx.fillStyle = "#2a2e33";
    ctx.fillRect(lx - 12, groundY - 10, 24, 10);
    ctx.fillRect(rx - 12, groundY - 10, 24, 10);
    // lamp heads
    ctx.fillStyle = "#1f2227";
    ctx.fillRect(lx - 14, topY - 14, 28, 14);
    ctx.fillRect(rx - 14, topY - 14, 28, 14);
    ctx.fillStyle = "#ffe9a8";
    ctx.fillRect(lx - 9, topY - 11, 18, 8);
    ctx.fillRect(rx - 9, topY - 11, 18, 8);
    // suspension ropes from lamp heads to banner top corners
    ctx.strokeStyle = "#caa46a"; ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(lx, topY - 2); ctx.lineTo(px - 6, pyTop - 6);
    ctx.moveTo(rx, topY - 2); ctx.lineTo(px + pw + 6, pyTop - 6);
    ctx.stroke();
    // matte-black banner backing with a slight sag (no white frame)
    const sag = 16;
    ctx.fillStyle = "#141418";
    ctx.beginPath();
    ctx.moveTo(px - 14, pyTop - 8);
    ctx.quadraticCurveTo(px + pw / 2, pyTop - 8 + sag, px + pw + 14, pyTop - 8);
    ctx.lineTo(px + pw + 14, pyTop + ph + 8);
    ctx.quadraticCurveTo(px + pw / 2, pyTop + ph + 8 + sag, px - 14, pyTop + ph + 8);
    ctx.closePath(); ctx.fill();
    // subtle edge sheen
    ctx.strokeStyle = "rgba(255,255,255,0.06)"; ctx.lineWidth = 2; ctx.stroke();
  }

  // Shared ad surface: a slim matte-black bezel framing the sponsor video/image
  // (which fills the panel edge-to-edge), or the bilingual "ADS HERE" placeholder.
  drawAdPanelContent(ctx, px, pyTop, pw, ph, ad) {
    // Slim matte-black frame around the screen.
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(px - 3, pyTop - 3, pw + 6, ph + 6);

    const tf = (ad && ad.transform) || {};
    const ix = px, iy = pyTop, iw = pw, ih = ph;  // media fills the panel edge-to-edge
    const vid = ad && ad.video;
    const vidReady = vid && vid.readyState >= 2 && vid.videoWidth > 0;
    const adImg = ad && ad.img;
    const imgReady = adImg && (adImg.complete ? adImg.naturalWidth > 0 : false);

    if (vidReady) {
      ctx.fillStyle = "#000"; ctx.fillRect(ix, iy, iw, ih);
      this._drawMediaInPanel(ctx, vid, vid.videoWidth, vid.videoHeight, ix, iy, iw, ih, tf);
      this._drawAdCaption(ctx, ad, px, pyTop, pw, ph);
      return;
    }
    if (imgReady) {
      ctx.fillStyle = "#000"; ctx.fillRect(ix, iy, iw, ih);
      this._drawMediaInPanel(ctx, adImg, adImg.naturalWidth, adImg.naturalHeight, ix, iy, iw, ih, tf);
      this._drawAdCaption(ctx, ad, px, pyTop, pw, ph);
      return;
    }

    // A sponsor video/image is configured but not decoded yet → show the Nica Bros
    // logo (centered, contained) so the billboard never looks blank while it loads.
    if (ad && (ad.video || ad.img)) {
      const lg = ctx.createLinearGradient(px, pyTop, px, pyTop + ph);
      lg.addColorStop(0, "#0c1a33"); lg.addColorStop(1, "#06101f");
      ctx.fillStyle = lg; ctx.fillRect(px, pyTop, pw, ph);
      const logo = this.brandLogo;
      if (logo && logo.complete && logo.naturalWidth > 0) {
        this._drawMediaInPanel(ctx, logo, logo.naturalWidth, logo.naturalHeight, px, pyTop, pw, ph, { fit: "contain", zoom: 0.92 });
      }
      this._drawAdCaption(ctx, ad, px, pyTop, pw, ph);
      return;
    }

    // Placeholder: matte dark screen + bilingual "ADS HERE" text
    const g = ctx.createLinearGradient(px, pyTop, px, pyTop + ph);
    g.addColorStop(0, "#1b1b24"); g.addColorStop(1, "#101016");
    ctx.fillStyle = g; ctx.fillRect(px, pyTop, pw, ph);
    ctx.save();
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillStyle = "#FFEB3B";
    let fs = 34;
    const maxW = pw - 26;
    ctx.font = `800 ${fs}px 'Baloo 2', system-ui, sans-serif`;
    while (ctx.measureText(this.S.adHere).width > maxW && fs > 14) {
      fs -= 1; ctx.font = `800 ${fs}px 'Baloo 2', system-ui, sans-serif`;
    }
    ctx.fillText(this.S.adHere, px + pw / 2, pyTop + ph / 2 - 6);
    ctx.font = "700 15px 'Baloo 2', system-ui, sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.55)";
    ctx.fillText(this.S.adSub, px + pw / 2, pyTop + ph / 2 + 24);
    ctx.restore();
  }

  // Draw an image/video inside the billboard panel honoring fit + zoom + pan transform.
  _drawMediaInPanel(ctx, media, nw, nh, ix, iy, iw, ih, tf) {
    if (!nw || !nh) return;
    const fit = tf.fit === "contain" ? "contain" : "cover";
    const base = fit === "contain" ? Math.min(iw / nw, ih / nh) : Math.max(iw / nw, ih / nh);
    const scale = base * (tf.zoom || 1);
    const dw = nw * scale, dh = nh * scale;
    const overX = dw - iw, overY = dh - ih;
    const dx = ix + (iw - dw) / 2 + (tf.offset_x || 0) * (overX > 0 ? overX / 2 : 0);
    const dy = iy + (ih - dh) / 2 + (tf.offset_y || 0) * (overY > 0 ? overY / 2 : 0);
    ctx.save();
    ctx.beginPath(); ctx.rect(ix, iy, iw, ih); ctx.clip();
    if (fit === "contain") { ctx.fillStyle = "#0c0c14"; ctx.fillRect(ix, iy, iw, ih); }
    try { ctx.drawImage(media, dx, dy, dw, dh); } catch (e) {}
    ctx.restore();
  }

  // Bilingual sponsor caption strip along the bottom of the billboard.
  _drawAdCaption(ctx, ad, px, pyTop, pw, ph) {
    if (!ad) return;
    const cap = this.lang === "en"
      ? (ad.captionEn || ad.captionEs)
      : (ad.captionEs || ad.captionEn);
    if (!cap) return;
    const barH = 20;
    const by = pyTop + ph - 5 - barH;
    ctx.save();
    ctx.beginPath(); ctx.rect(px, by, pw, barH); ctx.clip();
    ctx.fillStyle = "rgba(0,0,0,0.55)";
    ctx.fillRect(px, by, pw, barH);
    ctx.fillStyle = "#fff";
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    let fs = 14;
    ctx.font = `700 ${fs}px 'Baloo 2', system-ui, sans-serif`;
    while (ctx.measureText(cap).width > pw - 16 && fs > 9) {
      fs -= 1; ctx.font = `700 ${fs}px 'Baloo 2', system-ui, sans-serif`;
    }
    ctx.fillText(cap, px + pw / 2, by + barH / 2);
    ctx.restore();
  }

  drawBgSegments(camX, level) {
    const ctx = this.ctx;
    const parallax = 0.55;
    const segW = WIDTH;
    const scroll = camX * parallax;
    const pairs = (level.bgSegs || []).map((k) => ({ k, img: this.assets[k] })).filter((p) => ready(p.img));
    if (pairs.length === 0) return;
    // Depth parallax for the Trees-of-Life plaza: draw the empty-plaza "far"
    // layer as the base and overlay the cut-out trees ("near") slightly faster.
    const tp = level.treeParallax;
    const useTP = !!tp && ready(this.assets[tp.far1]) && ready(this.assets[tp.far2])
      && ready(this.assets[tp.near1]) && ready(this.assets[tp.near2]);
    const pinEnd = level && level.bgEndPinned && pairs.length > 1;
    let cyc = pairs;
    let endTile = -1;
    if (pinEnd) {
      cyc = pairs.slice(0, pairs.length - 1);
      const bgEnd = ((level.width || 0) - WIDTH) * parallax;
      endTile = Math.floor(bgEnd / segW);
    }
    const keyAt = (i) => {
      if (pinEnd && i === endTile) return pairs[pairs.length - 1];
      const n = cyc.length;
      // Featured levels show each background once in order and HOLD on the last
      // one (night Toña) to the end — never wrap back to the intro daytime scene.
      if (this.level.featured) return cyc[Math.max(0, Math.min(n - 1, i))];
      return cyc[((i % n) + n) % n];
    };
    const first = Math.floor(scroll / segW);
    // Base pass: plaza tiles (far layer swapped in where parallax applies).
    for (let i = first; i * segW < scroll + WIDTH; i++) {
      const { k, img } = keyAt(i);
      const x = Math.round(i * segW - scroll);
      if (useTP && (k === tp.seg1 || k === tp.seg2)) {
        this.drawImageCover(this.assets[k === tp.seg1 ? tp.far1 : tp.far2], x, 0, segW, HEIGHT);
      } else if (this.level.featured) {
        this.drawSegBg(img, x, segW);
      } else {
        this.drawImageCover(img, x, 0, segW, HEIGHT);
      }
    }
    // Overlay pass: cut-out trees, moving a touch faster for a 3D feel.
    if (useTP) {
      const depth = 0.12;
      for (let i = first - 1; i * segW < scroll + WIDTH + segW; i++) {
        if (keyAt(i).k !== tp.seg1) continue; // band starts at seg1 (2 tiles wide)
        const bandX = Math.round(i * segW - scroll);
        const shift = (bandX + segW - WIDTH / 2) * depth; // foreground: leads the plaza
        ctx.save();
        ctx.beginPath();
        ctx.rect(bandX, 0, segW * 2, HEIGHT);
        ctx.clip();
        this.drawImageCover(this.assets[tp.near1], bandX + shift, 0, segW, HEIGHT);
        this.drawImageCover(this.assets[tp.near2], bandX + segW + shift, 0, segW, HEIGHT);
        ctx.restore();
      }
    }
    // Crossfade adjacent segment photos so they flow into each other smoothly
    // (instead of a hard cut). Only interior seams; skip the tree-parallax plaza.
    const B = Math.round(segW * (this.level.featured ? 0.11 : 0.24));
    if (!this._seamOff) this._seamOff = document.createElement("canvas");
    const off = this._seamOff;
    if (off.width !== WIDTH || off.height !== HEIGHT) { off.width = WIDTH; off.height = HEIGHT; }
    const octx = off.getContext("2d");
    for (let i = first; i * segW < scroll + WIDTH; i++) {
      const sx = Math.round(i * segW - scroll);
      if (sx <= 0 || sx >= WIDTH) continue; // interior seam only
      const R = keyAt(i);
      const Rimg = R && this.assets[R.k];
      if (!ready(Rimg)) continue;
      if (useTP && (R.k === tp.seg1 || R.k === tp.seg2)) continue;
      octx.clearRect(0, 0, WIDTH, HEIGHT);
      if (this.level.featured) {
        const bottom = GAME.GROUND_Y + 2;
        this._coverRect(octx, Rimg, sx - B, 0, segW + B, bottom);
        octx.fillStyle = "rgba(18,22,38,0.32)";
        octx.fillRect(sx - B, 0, segW + B, bottom);
      } else {
        const dx = sx - B, dw = segW + B, dr = dw / HEIGHT;
        const ir = Rimg.naturalWidth / Rimg.naturalHeight;
        let sw, sh, sxi, syi;
        if (ir > dr) { sh = Rimg.naturalHeight; sw = sh * dr; sxi = (Rimg.naturalWidth - sw) / 2; syi = 0; }
        else { sw = Rimg.naturalWidth; sh = sw / dr; sxi = 0; syi = (Rimg.naturalHeight - sh) / 2; }
        octx.drawImage(Rimg, sxi, syi, sw, sh, dx, 0, dw, HEIGHT);
      }
      octx.globalCompositeOperation = "destination-in";
      const g = octx.createLinearGradient(sx - B, 0, sx, 0);
      g.addColorStop(0, "rgba(0,0,0,0)");
      g.addColorStop(1, "rgba(0,0,0,1)");
      octx.fillStyle = g;
      octx.fillRect(sx - B, 0, B, HEIGHT);
      octx.fillStyle = "rgba(0,0,0,1)";
      octx.fillRect(sx, 0, WIDTH - sx, HEIGHT);
      octx.globalCompositeOperation = "source-over";
      ctx.drawImage(off, 0, 0);
    }
    // Dusk wash: warm the whole scene as the player advances through the sunset
    // stretches, so the day → sunset → night backgrounds feel like one evening.
    if (this.level.featured) {
      const span = Math.max(1, this.level.width - WIDTH);
      const p = Math.max(0, Math.min(1, this.camera.x / span));
      if (p > 0.32) {
        const t = (p - 0.32) / 0.68; // 0 at 32% → 1 at the end
        ctx.save();
        ctx.globalCompositeOperation = "multiply";
        const r = Math.round(255 - 70 * t), gc = Math.round(150 - 70 * t), bc = Math.round(90 + 40 * t);
        ctx.fillStyle = `rgba(${r},${gc},${bc},${(0.10 + 0.22 * t).toFixed(3)})`;
        ctx.fillRect(0, 0, WIDTH, HEIGHT);
        ctx.globalCompositeOperation = "source-over";
        const glow = ctx.createLinearGradient(0, 0, 0, HEIGHT * 0.6);
        glow.addColorStop(0, `rgba(255,${Math.round(150 - 60 * t)},60,${(0.14 * t).toFixed(3)})`);
        glow.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = glow;
        ctx.fillRect(0, 0, WIDTH, HEIGHT * 0.6);
        ctx.restore();
        // Glowing string-light sparkles / fireflies fading in through the night.
        const night = Math.max(0, (t - 0.35) / 0.65);
        if (night > 0.02) {
          ctx.save();
          ctx.globalCompositeOperation = "lighter";
          const f = this.frame;
          for (let i = 0; i < 46; i++) {
            const sxp = (i * 197) % WIDTH;
            const syp = 34 + ((i * 113) % Math.round(HEIGHT * 0.62));
            const tw = 0.5 + 0.5 * Math.sin(f * 0.06 + i * 1.7);
            const a = night * (0.22 + 0.55 * tw);
            const rad = (2 + 2.4 * tw) * 3;
            const warm = i % 5 === 0 ? "255,220,120" : (i % 3 === 0 ? "180,255,150" : "255,190,80");
            const grd = ctx.createRadialGradient(sxp, syp, 0, sxp, syp, rad);
            grd.addColorStop(0, `rgba(${warm},${a.toFixed(3)})`);
            grd.addColorStop(1, `rgba(${warm},0)`);
            ctx.fillStyle = grd;
            ctx.beginPath(); ctx.arc(sxp, syp, rad, 0, Math.PI * 2); ctx.fill();
          }
          ctx.restore();
        }
      }
    }

  }

  drawBgImage(img, camX) {
    const ctx = this.ctx;
    const scale = HEIGHT / img.naturalHeight;
    const sw = img.naturalWidth * scale;
    const off = -((camX * 0.5) % sw);
    for (let x = off - sw; x < WIDTH + sw; x += sw) ctx.drawImage(img, x, 0, sw, HEIGHT);
  }

  drawPlatform(ctx, pl) {
    if (pl.type === "ground") {
      if (this.level.road) {
        // Dirt road everywhere; cobblestone + crosswalk only on the FIRST and
        // LAST background stretches (near El Timón). World-space.
        const cw = 960;
        const endEdge = this.level.width - cw;
        const startK = Math.max(0, Math.floor((this.camera.x - cw) / cw));
        const endK = Math.floor((this.camera.x + WIDTH) / cw);
        for (let k = startK; k <= endK; k++) {
          const x0 = k * cw;
          const cx = Math.max(x0, pl.x);
          const cright = Math.min(x0 + cw, pl.x + pl.w);
          const w0 = cright - cx;
          if (w0 <= 0) continue;
          const edge = x0 < cw || x0 >= endEdge - 1;
          this.drawRoadChunk(ctx, cx, pl.y, w0, pl.h, edge ? "cobble" : "dirt", edge);
        }
        return;
      }
      ctx.fillStyle = this.level.ground; ctx.fillRect(pl.x, pl.y, pl.w, pl.h);
      ctx.fillStyle = this.level.groundTop; ctx.fillRect(pl.x, pl.y, pl.w, 12);
      ctx.fillStyle = "rgba(0,0,0,0.12)";
      for (let x = pl.x; x < pl.x + pl.w; x += 40) ctx.fillRect(x, pl.y + 22, 18, 6);
    } else {
      ctx.fillStyle = "#b5651d"; ctx.fillRect(pl.x, pl.y, pl.w, pl.h);
      ctx.fillStyle = "#d2853a"; ctx.fillRect(pl.x, pl.y, pl.w, 5);
      ctx.fillStyle = "rgba(0,0,0,0.25)";
      for (let x = pl.x + 6; x < pl.x + pl.w; x += 22) ctx.fillRect(x, pl.y + 4, 2, pl.h - 6);
    }
  }

  drawRoadChunk(ctx, x, y, w, h, style, crosswalk) {
    if (style === "dirt") {
      ctx.fillStyle = "#7c5a35"; ctx.fillRect(x, y, w, h);
      ctx.fillStyle = "#916b41"; ctx.fillRect(x, y, w, 10);
      ctx.fillStyle = "#684a2b"; ctx.fillRect(x, y + 10, w, 3);
      // two soft wheel ruts
      ctx.fillStyle = "rgba(70,48,26,0.55)";
      ctx.fillRect(x, y + 26, w, 4); ctx.fillRect(x, y + 44, w, 4);
      // scattered pebbles (deterministic by world x so they don't flicker)
      for (let dx = x - (x % 26); dx < x + w; dx += 26) {
        const r = (Math.sin(dx * 12.9898) * 43758.5453) % 1;
        ctx.fillStyle = r > 0.5 ? "rgba(210,180,140,0.6)" : "rgba(60,42,24,0.5)";
        ctx.fillRect(dx + ((dx * 7) % 18), y + 16 + ((dx * 3) % 30), 5, 4);
      }
    } else if (style === "cobble") {
      ctx.fillStyle = "#8b9198"; ctx.fillRect(x, y, w, h);
      ctx.fillStyle = "#9aa0a7"; ctx.fillRect(x, y, w, 8);
      // stone grid with mortar gaps, offset alternate rows
      const sw = 30, sh = 15;
      for (let row = 0, ry = y + 6; ry < y + h; row++, ry += sh + 3) {
        const off = row % 2 ? sw / 2 : 0;
        for (let sx = x - (((x + off) % (sw + 3))); sx < x + w; sx += sw + 3) {
          ctx.fillStyle = (row + Math.round(sx / (sw + 3))) % 2 ? "#7e848b" : "#868c93";
          ctx.fillRect(sx + off + 2, ry, sw, sh);
        }
      }
      // painted crosswalk near El Timón (very start of the level)
      if (crosswalk) {
        ctx.fillStyle = "rgba(255,255,255,0.9)";
        for (let bx = x + 150; bx < x + 430; bx += 40) ctx.fillRect(bx, y + 6, 22, h - 12);
      }
    } else {
      // asphalt
      ctx.fillStyle = "#3b3f46"; ctx.fillRect(x, y, w, h);
      ctx.fillStyle = "#4c515a"; ctx.fillRect(x, y, w, 10);
      ctx.fillStyle = "#2c2f35"; ctx.fillRect(x, y + 10, w, 3);
      ctx.fillStyle = "#f6c945";
      for (let dx = x - (x % 96); dx < x + w; dx += 96) {
        const seg = Math.max(0, Math.min(52, x + w - dx));
        if (seg > 4) ctx.fillRect(Math.max(dx, x), y + 42, Math.min(52, seg), 6);
      }
    }
  }
}
